import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FaChevronDown, FaChevronRight, FaExternalLinkAlt, FaExclamationTriangle, FaUndo, FaPlay, FaTimes, FaStop, FaEnvelope, FaBan, FaRegStickyNote } from "react-icons/fa"
import { FaSortUp, FaSortDown } from "react-icons/fa6"
import { Box, CloseButton, Dialog, Drawer, HStack, Icon, IconButton, Input, Portal, Spinner, Stack, Table, Image, Textarea } from "@chakra-ui/react"
import { FaArrowUp } from "react-icons/fa"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Section, Text, toaster } from "@/components"
import { useFmvReview, useFmvProofFiles, useFmvManagers, useDmFmvReview, useFmvStagingList, useMicrosoftConnection, useClientEmails, useClientEmailAttachments, useProofStatuses, useFmvActivity, useFmvActivityUnseenCount, useFmvChangeReport } from "@/hooks"
import { cartaExtract, cartaConfirm, cartaCloseBrowser, previewFmvOutreach, sendFmvOutreach, sendBulkFmvOutreach, setDoNotContact as apiSetDoNotContact, clearDoNotContact as apiClearDoNotContact, blockCompany as apiBlockCompany, unblockCompany as apiUnblockCompany, getOutreachStatuses, markOutreachStatus as apiMarkOutreachStatus, getFmvProofFiles, applyFmvFix, getFmvPunchlist, completePunchlistItem as completePunchlistItemApi, reopenPunchlistItem as reopenPunchlistItemApi, resyncIrrFmv, sendProofReminder, uploadAttachmentToDropbox, syncProofStatuses, recheckProofStatus, stageAttachmentAs409A, uploadEmailToDropbox, stageEmailAs409A, saveDmFmvNote as apiSaveDmFmvNote, markFmvActivitySeen as apiMarkFmvActivitySeen, correctFmvDate } from "@/api/companies"
import type { ProofStatusEntry } from "@/api/companies"
import { useAuth } from "@/contexts"
import type { FmvReviewEntry, FmvProofFile, ReconciliationStatus, DmFmvEntry, CartaExtractionResult, FmvOutreachStatus, FmvActivityItem } from "@/types"
import type { DealEmail } from "@/types"
import type { DealManager } from "@/types/user"
import { EmailDetailModal } from "@/features/esoPerson/companies/components/deal/EmailDetailModal"

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDollars(v: number | null): string {
  if (v == null || v === 0) return "—"
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(2)}`
}

function formatFmv(v: number | null): string {
  if (v == null) return "—"
  return `$${v.toFixed(2)}`
}

function formatDate(d: string | null): string {
  if (!d) return "—"
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  } catch {
    return d
  }
}

function ageColor(years: number): string {
  if (years < 1) return "#22c55e"    // green
  if (years < 2) return "#f59e0b"    // yellow/amber
  if (years < 3) return "#f97316"    // orange
  return "#ef4444"                    // red
}

function ageBg(years: number): string {
  if (years < 1) return "rgba(34,197,94,0.1)"
  if (years < 2) return "rgba(245,158,11,0.1)"
  if (years < 3) return "rgba(249,115,22,0.1)"
  return "rgba(239,68,68,0.1)"
}

function ageLabel(years: number): string {
  if (years < 0.1) return "Current"
  if (years < 1) return `${Math.round(years * 12)}mo`
  return `${years.toFixed(1)}yr`
}

/** Two values are "approximately equal" if abs diff ≤ $0.02 OR relative diff ≤ 1% */
function approxEqual(a: number, b: number): boolean {
  const absDiff = Math.abs(a - b)
  if (absDiff <= 0.02) return true
  const denom = Math.max(Math.abs(a), Math.abs(b))
  if (denom === 0) return true
  return (absDiff / denom) <= 0.01
}

/** Compute staleness discount from a date string (mirrors backend computeFmvStalenessDiscount) */
function computeStalenessFromDate(dateStr: string | null): { yearsStale: number; discountPct: number; factor: number } {
  if (!dateStr) return { yearsStale: 0, discountPct: 0, factor: 1.0 }
  const fmvDate = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - fmvDate.getTime()
  const yearsStale = diffMs / (365.25 * 24 * 60 * 60 * 1000)
  const discountPct = Math.min(Math.floor(yearsStale) * 10, 90)
  const factor = 1 - discountPct / 100
  return { yearsStale, discountPct, factor }
}

// ─── Reconciliation Badges ──────────────────────────────────────────────────────

type BadgeStyle = { bg: string; color: string; label: string }

function reconBadgeStyle(status: ReconciliationStatus): BadgeStyle {
  switch (status) {
    case "ok":              return { bg: "rgba(34,197,94,0.15)", color: "#22c55e", label: "OK" }
    case "stale_match":     return { bg: "rgba(59,130,246,0.15)", color: "#3b82f6", label: "DISCOUNT MATCH" }
    case "discount_needed": return { bg: "rgba(245,158,11,0.15)", color: "#f59e0b", label: "UPDATE DISCOUNT" }
    case "irr_mismatch":    return { bg: "rgba(239,68,68,0.15)", color: "#ef4444", label: "MISMATCH" }
    case "no_fmv":          return { bg: "rgba(107,114,128,0.15)", color: "#6b7280", label: "NO FMV" }
    case "no_irr":          return { bg: "rgba(107,114,128,0.15)", color: "#6b7280", label: "NO IRR" }
    case "written_off":     return { bg: "rgba(139,92,246,0.15)", color: "#8b5cf6", label: "WRITTEN OFF" }
  }
}

// ─── Level 2 Proof Reconciliation ───────────────────────────────────────────────

type ProofReconciliation = {
  suggestion: string
  severity: "green" | "blue" | "amber" | "red"
  detail: string
  dateCorrection?: {
    currentDate: string
    newDate: string
    proofFileName: string
  }
}

/** Two YYYY-MM-DD date strings are within N days of each other */
function datesWithinDays(a: string | null, b: string | null, days: number): boolean {
  if (!a || !b) return false
  const diff = Math.abs(new Date(a).getTime() - new Date(b).getTime())
  return diff <= days * 86_400_000
}

/** Fuzzy name comparison — handles typos like "Blumenstein" vs "Blumstein" */
function fuzzyNameMatch(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "")
  const na = norm(a), nb = norm(b)
  if (na === nb) return true
  if (na.length >= 3 && nb.length >= 3 && (na.startsWith(nb) || nb.startsWith(na))) return true
  const maxLen = Math.max(na.length, nb.length)
  if (maxLen === 0) return true
  return levenshteinDistance(na, nb) <= Math.max(2, Math.floor(maxLen * 0.15))
}

function levenshteinDistance(a: string, b: string): number {
  const m = a.length, n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  )
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[m][n]
}

function computeProofReconciliation(
  entry: ReconcilableEntry,
  proofFile: FmvProofFile | null,
): ProofReconciliation | null {
  if (!proofFile?.parsedValue) return null

  const { latestFmv, irrFmv } = entry
  const proofValue = proofFile.parsedValue

  // Compute staleness from both dates
  const proofStaleness = computeStalenessFromDate(proofFile.parsedDate)

  // Smart date alignment: strict 1-day OR (7-day window + value/name match)
  const strictDatesAligned = datesWithinDays(entry.latestFmvDate, proofFile.parsedDate, 1)
  const withinSevenDays = datesWithinDays(entry.latestFmvDate, proofFile.parsedDate, 7)
  const valueMatches = latestFmv != null && approxEqual(latestFmv, proofValue)
  const nameMatches = entry.fmvAddedBy && proofFile.parsedSource
    ? fuzzyNameMatch(entry.fmvAddedBy, proofFile.parsedSource) : false
  const datesAligned = strictDatesAligned || (withinSevenDays && (valueMatches || nameMatches))

  // When smart-matched by value but dates differ — offer date correction
  if (!strictDatesAligned && datesAligned && valueMatches && proofFile.parsedDate && entry.latestFmvDate) {
    const daysDiff = Math.round(Math.abs(new Date(entry.latestFmvDate).getTime() - new Date(proofFile.parsedDate).getTime()) / 86_400_000)
    return {
      suggestion: `Correct FMV date to ${formatDate(proofFile.parsedDate)}`,
      severity: "blue",
      detail: `FMV value ($${latestFmv!.toFixed(2)}) matches proof ($${proofValue.toFixed(2)}). DB date (${formatDate(entry.latestFmvDate)}) differs from proof date (${formatDate(proofFile.parsedDate)}) by ${daysDiff} day${daysDiff !== 1 ? "s" : ""}. Correct the date to align with the proof file.`,
      dateCorrection: {
        currentDate: entry.latestFmvDate,
        newDate: proofFile.parsedDate,
        proofFileName: proofFile.name,
      },
    }
  }

  // Determine which date is the latest data point (only when >1 day apart)
  const dbDateIsNewer = !datesAligned && entry.latestFmvDate && proofFile.parsedDate
    ? entry.latestFmvDate >= proofFile.parsedDate
    : false
  const proofDateIsNewer = !datesAligned && proofFile.parsedDate && entry.latestFmvDate
    ? proofFile.parsedDate > entry.latestFmvDate
    : false

  // Use DB staleness factor when DB date is newer; proof staleness otherwise
  const effectiveFactor = dbDateIsNewer ? entry.staleness.factor : proofStaleness.factor
  const discountedProof = Math.round(proofValue * effectiveFactor * 100) / 100

  const dbFactorDiffers = proofDateIsNewer && Math.abs(proofStaleness.factor - entry.staleness.factor) > 0.001

  const effectiveDiscountPct = Math.round((1 - effectiveFactor) * 100)

  // All three values present — do full comparison
  if (latestFmv != null && irrFmv != null) {
    const fmvMatchesProof = approxEqual(latestFmv, proofValue)
    const irrMatchesDiscountedProof = approxEqual(irrFmv, discountedProof)

    // DB date is newer than proof — DB 409A is the more recent data point
    if (dbDateIsNewer) {
      const addedByNote = entry.fmvAddedBy ? ` Updated by ${entry.fmvAddedBy}.` : ""
      const dbDiscounted = entry.discountedFmv ?? (latestFmv != null ? Math.round(latestFmv * entry.staleness.factor * 100) / 100 : null)
      const dbDiscountPct = Math.round((1 - entry.staleness.factor) * 100)
      const irrMatchesDb = dbDiscounted != null && approxEqual(irrFmv, dbDiscounted)
      if (irrMatchesDb) {
        return {
          suggestion: "All values reconcile",
          severity: "green",
          detail: `DB 409A ($${latestFmv!.toFixed(2)}, ${formatDate(entry.latestFmvDate)}) is newer than latest proof file (${formatDate(proofFile.parsedDate)}).${addedByNote} IRR FMV ($${irrFmv.toFixed(2)}) matches expected ($${dbDiscounted!.toFixed(2)}).`,
        }
      }
      const missingProofNote = ` Obtain proof file for the ${formatDate(entry.latestFmvDate)} valuation.`
      return {
        suggestion: `IRR FMV should be $${dbDiscounted != null ? dbDiscounted.toFixed(2) : "?"}`,
        severity: "amber",
        detail: `DB 409A ($${latestFmv!.toFixed(2)}, ${formatDate(entry.latestFmvDate)})${addedByNote} is newer than proof file (${formatDate(proofFile.parsedDate)}). Expected IRR: $${dbDiscounted != null ? dbDiscounted.toFixed(2) : "?"} (${dbDiscountPct}% discount), actual: $${irrFmv.toFixed(2)}.${missingProofNote}`,
      }
    }

    // Proof date is newer and changes the staleness discount
    if (proofDateIsNewer && dbFactorDiffers) {
      if (fmvMatchesProof && irrMatchesDiscountedProof) {
        return {
          suggestion: `Update 409A date to ${formatDate(proofFile.parsedDate)}`,
          severity: "amber",
          detail: `IRR FMV ($${irrFmv.toFixed(2)}) is correct using proof file date (${formatDate(proofFile.parsedDate)}, ${proofStaleness.discountPct}% discount). Update 409A date from ${formatDate(entry.latestFmvDate)} to ${formatDate(proofFile.parsedDate)} so the discount aligns.`,
        }
      }
      return {
        suggestion: `Update 409A date & IRR FMV to $${discountedProof.toFixed(2)}`,
        severity: "amber",
        detail: `Proof file (${formatDate(proofFile.parsedDate)}) is newer than DB record (${formatDate(entry.latestFmvDate)}). Using proof date: ${proofStaleness.discountPct}% discount vs current ${entry.staleness.discountPct}%. Update 409A date to ${formatDate(proofFile.parsedDate)} and set IRR FMV to $${discountedProof.toFixed(2)}.`,
      }
    }

    // Standard comparison — dates align or no significant factor difference
    if (fmvMatchesProof && irrMatchesDiscountedProof) {
      return {
        suggestion: "All values reconcile",
        severity: "green",
        detail: `409A ($${latestFmv.toFixed(2)}) matches proof file ($${proofValue.toFixed(2)}). IRR FMV ($${irrFmv.toFixed(2)}) matches discounted value ($${discountedProof.toFixed(2)}).`,
      }
    }

    if (fmvMatchesProof && !irrMatchesDiscountedProof) {
      return {
        suggestion: `IRR FMV should be $${discountedProof.toFixed(2)}`,
        severity: "amber",
        detail: `409A ($${latestFmv.toFixed(2)}) matches proof file ($${proofValue.toFixed(2)}), but IRR FMV ($${irrFmv.toFixed(2)}) does not match the expected discounted value ($${discountedProof.toFixed(2)}).`,
      }
    }

    if (!fmvMatchesProof) {
      return {
        suggestion: `Update 409A to $${proofValue.toFixed(2)} & IRR FMV to $${discountedProof.toFixed(2)}`,
        severity: "amber",
        detail: `Proof file ($${proofValue.toFixed(2)} from ${formatDate(proofFile.parsedDate)}) differs from DB 409A ($${latestFmv.toFixed(2)}). Update 409A value and set IRR FMV to $${discountedProof.toFixed(2)} (${effectiveDiscountPct}% discount).`,
      }
    }

    // All three disagree
    return {
      suggestion: "Manual reconciliation needed",
      severity: "red",
      detail: `All three values differ — 409A: $${latestFmv.toFixed(2)}, IRR FMV: $${irrFmv.toFixed(2)}, Proof: $${proofValue.toFixed(2)} (discounted: $${discountedProof.toFixed(2)}).`,
    }
  }

  // Only 409A + proof (no IRR)
  if (latestFmv != null && irrFmv == null) {
    if (proofDateIsNewer && dbFactorDiffers && approxEqual(latestFmv, proofValue)) {
      return {
        suggestion: `Update 409A date & set IRR FMV to $${discountedProof.toFixed(2)}`,
        severity: "amber",
        detail: `Proof file (${formatDate(proofFile.parsedDate)}) is newer than DB record (${formatDate(entry.latestFmvDate)}). Update 409A date and set IRR FMV to $${discountedProof.toFixed(2)} (${effectiveDiscountPct}% discount).`,
      }
    }
    if (approxEqual(latestFmv, proofValue)) {
      return {
        suggestion: `IRR FMV missing — should be $${discountedProof.toFixed(2)}`,
        severity: "amber",
        detail: `409A ($${latestFmv.toFixed(2)}) matches proof file ($${proofValue.toFixed(2)}). IRR FMV needs to be set to $${discountedProof.toFixed(2)}.`,
      }
    }
    return {
      suggestion: `Update 409A to $${proofValue.toFixed(2)} & set IRR FMV to $${discountedProof.toFixed(2)}`,
      severity: "amber",
      detail: `Proof file ($${proofValue.toFixed(2)} from ${formatDate(proofFile.parsedDate)}) differs from DB 409A ($${latestFmv.toFixed(2)}). Update 409A and set IRR FMV to $${discountedProof.toFixed(2)}.`,
    }
  }

  // Only IRR + proof (no 409A)
  if (latestFmv == null && irrFmv != null) {
    if (approxEqual(irrFmv, discountedProof)) {
      return {
        suggestion: `409A missing — should be $${proofValue.toFixed(2)}`,
        severity: "amber",
        detail: `IRR FMV ($${irrFmv.toFixed(2)}) matches discounted proof value ($${discountedProof.toFixed(2)}). Set 409A to $${proofValue.toFixed(2)}.`,
      }
    }
    return {
      suggestion: `Set 409A to $${proofValue.toFixed(2)} & IRR FMV to $${discountedProof.toFixed(2)}`,
      severity: "amber",
      detail: `Proof file value is $${proofValue.toFixed(2)} (${formatDate(proofFile.parsedDate)}). Set 409A to $${proofValue.toFixed(2)} and IRR FMV to $${discountedProof.toFixed(2)} (${effectiveDiscountPct}% discount).`,
    }
  }

  return null
}

const severityColors: Record<ProofReconciliation["severity"], { bg: string; border: string; text: string }> = {
  green: { bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.3)", text: "#22c55e" },
  blue:  { bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.3)", text: "#3b82f6" },
  amber: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.3)", text: "#f59e0b" },
  red:   { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.3)", text: "#ef4444" },
}

// ─── Level 1 Suggestion (no Dropbox calls) ──────────────────────────────────────

function getLevel1Suggestion(entry: ReconcilableEntry): string | null {
  switch (entry.reconciliation) {
    case "irr_mismatch":
      return "FMV mismatch"
    case "discount_needed":
      return entry.discountedFmv != null
        ? `Update discount to $${entry.discountedFmv.toFixed(2)}`
        : "Update discount needed"
    case "no_irr":
      return entry.discountedFmv != null
        ? `Set IRR to $${entry.discountedFmv.toFixed(2)}`
        : "Set IRR FMV"
    case "no_fmv":
      return "Find 409A value"
    default:
      return null
  }
}

// ─── Shared type for ProofFilesRow / fix modal (fields common to both FmvReviewEntry and DmFmvEntry) ──

type ReconcilableEntry = {
  companyId: number
  companyName: string
  companyFolderPath: string | null
  latestFmv: number | null
  latestFmvId: number | null
  latestFmvDate: string | null
  irrFmv: number | null
  staleness: { yearsStale: number; discountPct: number; factor: number }
  reconciliation: ReconciliationStatus
  discountedFmv: number | null
  isWrittenOff: boolean
  hasPendingPunchlist: boolean
  punchlistSuggestedFmv?: number | null
  fmvAddedBy?: string | null
  proofUploadDm?: { id: number; name: string; shortName: string; email: string } | null
}

// ─── Action Types ────────────────────────────────────────────────────────────────

type ActionType = "execute" | "mitigate" | "undo"
type ConfirmAction = { entry: ReconcilableEntry; type: ActionType }
type FixOption = "update_irr" | "update_409a" | "update_both"
type FixPayload = {
  fixOption: FixOption
  irrTarget?: string
  fmvTarget?: string
  fmvName?: string
  fmvComment?: string
  fmvDate?: string
  proofFileName?: string
}

/** Which button to show based on reconciliation status */
function getActionType(status: ReconciliationStatus): "execute" | "mitigate" | null {
  switch (status) {
    case "irr_mismatch":
    case "discount_needed":
    case "no_irr":
      return "execute"
    case "no_fmv":
      return "mitigate"
    default:
      return null
  }
}

// ─── Sorting ────────────────────────────────────────────────────────────────────

type SortKey = "company" | "fmv" | "date" | "age" | "irrFmv" | "discFmv" | "status" | "deals" | "portfolio" | "invested"
type SortDir = "asc" | "desc"

const STATUS_SORT_ORDER: Record<ReconciliationStatus, number> = {
  irr_mismatch: 0,
  discount_needed: 1,
  no_fmv: 2,
  no_irr: 3,
  written_off: 4,
  stale_match: 5,
  ok: 6,
}

function getSortValue(entry: FmvReviewEntry, key: SortKey): string | number | null {
  switch (key) {
    case "company":   return entry.companyName.toLowerCase()
    case "fmv":       return entry.latestFmv
    case "date":      return entry.latestFmvDate
    case "age":       return entry.staleness.yearsStale
    case "irrFmv":    return entry.irrFmv
    case "discFmv":   return entry.discountedFmv
    case "status":    return STATUS_SORT_ORDER[entry.reconciliation]
    case "deals":     return entry.activeDealCount
    case "portfolio": return entry.portfolioDealCount
    case "invested":  return entry.totalInvested
  }
}

function compareSortValues(a: string | number | null, b: string | number | null, dir: SortDir): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  const cmp = a < b ? -1 : a > b ? 1 : 0
  return dir === "asc" ? cmp : -cmp
}

// Column widths shared between fixed header table and scrollable body table
//               chevron  Company     CurrFMV  Date     Age      IRR FMV  Disc.FMV Status   Deals    Portfolio Invested
const COL_WIDTHS = ["30px", "18%", "9%", "9%", "8%", "9%", "9%", "16%", "6%", "8%", "8%"]

const ColGroup = () => (
  <colgroup>
    {COL_WIDTHS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
)

const SortableHeader = ({
  label, sortKey, currentKey, currentDir, onSort, textAlign,
}: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  currentDir: SortDir
  onSort: (key: SortKey) => void
  textAlign?: "left" | "right"
}) => {
  const isActive = currentKey === sortKey
  return (
    <Table.ColumnHeader
      textAlign={textAlign}
      cursor="pointer"
      onClick={() => onSort(sortKey)}
      _hover={{ color: "white" }}
      userSelect="none"
    >
      <HStack gap={1} justifyContent={textAlign === "right" ? "flex-end" : "flex-start"}>
        <Text fontSize="sm">{label}</Text>
        {isActive && (
          <Icon
            as={(currentDir === "asc" ? FaSortUp : FaSortDown) as React.ElementType}
            boxSize="10px"
            color="white"
          />
        )}
      </HStack>
    </Table.ColumnHeader>
  )
}

// ─── Proof Files Sub-component ──────────────────────────────────────────────────

const ProofFilesRow = ({ entry, onRequestFix, onCorrectDate }: { entry: ReconcilableEntry; onRequestFix?: (entry: ReconcilableEntry) => void; onCorrectDate?: (entry: ReconcilableEntry, newDate: string, proofFileName: string) => void }) => {
  const { data: files, isLoading, isError } = useFmvProofFiles(entry.companyFolderPath)

  const openPreview = (fileName: string) => {
    if (!entry.companyFolderPath) return
    const params = new URLSearchParams({ folderPath: entry.companyFolderPath, fileName })
    window.open(`${process.env.REACT_APP_SERVER_URL}/fmv-review/preview?${params}`, "_blank")
  }

  // Level 2 reconciliation — computed when proof files load
  const mostRecentProof = files && files.length > 0 ? files[0] : null
  const proofRecon = useMemo(
    () => computeProofReconciliation(entry, mostRecentProof),
    [entry, mostRecentProof],
  )

  // Proof-date-based staleness for display in the summary card
  const proofStaleness = mostRecentProof?.parsedDate ? computeStalenessFromDate(mostRecentProof.parsedDate) : null
  const proofDateIsNewer = mostRecentProof?.parsedDate && entry.latestFmvDate
    ? mostRecentProof.parsedDate > entry.latestFmvDate
    : false
  const showProofDiscount = proofDateIsNewer && proofStaleness != null
    && Math.abs(proofStaleness.factor - entry.staleness.factor) > 0.001

  if (!entry.companyFolderPath) {
    return (
      <Text fontSize="xs" color="text.muted" pl={8} py={2}>
        No Dropbox folder path available
      </Text>
    )
  }

  if (isLoading) {
    return (
      <HStack gap={2} pl={8} py={2}>
        <Spinner size="xs" />
        <Text fontSize="xs" color="text.muted">Loading 409A files...</Text>
      </HStack>
    )
  }

  if (isError) {
    return (
      <Text fontSize="xs" color="#ef4444" pl={8} py={2}>
        Failed to load files
      </Text>
    )
  }

  if (!files || files.length === 0) {
    return (
      <Text fontSize="xs" color="text.muted" pl={8} py={2}>
        No 409A files found
      </Text>
    )
  }

  return (
    <Stack gap={2} pl={8} py={2} pr={4}>
      {/* Reconciliation summary card */}
      {entry.reconciliation !== "ok" && (
        <Box
          borderRadius="md"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          bg="rgba(255,255,255,0.03)"
          p={3}
          mb={1}
        >
          <HStack gap={4} flexWrap="wrap" mb={proofRecon ? 2 : 0}>
            <Stack gap={0}>
              <Text fontSize="xs" color="text.muted">409A (Raw)</Text>
              <Text fontSize="sm" fontWeight="600">{formatFmv(entry.latestFmv)}</Text>
            </Stack>
            <Text fontSize="sm" color="text.muted">×</Text>
            <Stack gap={0}>
              <Text fontSize="xs" color="text.muted">Discount ({formatDate(entry.latestFmvDate)})</Text>
              <Text fontSize="sm" fontWeight="600" color={showProofDiscount ? "#f59e0b" : undefined}>
                {(entry.staleness.factor * 100).toFixed(0)}%
                {showProofDiscount && (
                  <Text as="span" fontSize="xs" color="#22c55e" ml={1}>
                    → {(proofStaleness!.factor * 100).toFixed(0)}%
                  </Text>
                )}
              </Text>
            </Stack>
            <Text fontSize="sm" color="text.muted">=</Text>
            <Stack gap={0}>
              <Text fontSize="xs" color="text.muted">Expected IRR</Text>
              <Text fontSize="sm" fontWeight="600" color="#3b82f6">
                {formatFmv(entry.discountedFmv)}
                {showProofDiscount && mostRecentProof?.parsedValue != null && (
                  <Text as="span" fontSize="xs" color="#22c55e" ml={1}>
                    → {formatFmv(Math.round(mostRecentProof.parsedValue * proofStaleness!.factor * 100) / 100)}
                  </Text>
                )}
              </Text>
            </Stack>
            <Text fontSize="sm" color="text.muted">vs</Text>
            <Stack gap={0}>
              <Text fontSize="xs" color="text.muted">Actual IRR</Text>
              <Text fontSize="sm" fontWeight="600" color={entry.reconciliation === "irr_mismatch" || entry.reconciliation === "discount_needed" ? "#f59e0b" : "#22c55e"}>
                {formatFmv(entry.irrFmv)}
              </Text>
            </Stack>
            {mostRecentProof?.parsedValue != null && (
              <>
                <Text fontSize="sm" color="text.muted">|</Text>
                <Stack gap={0}>
                  <Text fontSize="xs" color="text.muted">Proof ({formatDate(mostRecentProof.parsedDate)})</Text>
                  <Text fontSize="sm" fontWeight="600" color="#a78bfa">${mostRecentProof.parsedValue.toFixed(2)}</Text>
                </Stack>
              </>
            )}
          </HStack>

          {/* Level 2 suggestion banner */}
          {proofRecon && (
            <Box
              mt={1}
              px={3}
              py={2}
              borderRadius="md"
              bg={severityColors[proofRecon.severity].bg}
              border="1px solid"
              borderColor={severityColors[proofRecon.severity].border}
            >
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="700" color={severityColors[proofRecon.severity].text} mb={0.5}>
                    {proofRecon.suggestion}
                  </Text>
                  <Text fontSize="xs" color="text.muted">
                    {proofRecon.detail}
                  </Text>
                </Box>
                {proofRecon.dateCorrection && onCorrectDate && (
                  <Box
                    as="button"
                    px={2}
                    py={0.5}
                    borderRadius="sm"
                    fontSize="10px"
                    fontWeight="700"
                    flexShrink={0}
                    bg="rgba(59,130,246,0.12)"
                    color="#3b82f6"
                    border="1px solid rgba(59,130,246,0.3)"
                    cursor="pointer"
                    _hover={{ bg: "rgba(59,130,246,0.2)" }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      onCorrectDate(entry, proofRecon.dateCorrection!.newDate, proofRecon.dateCorrection!.proofFileName)
                    }}
                  >
                    Correct Date
                  </Box>
                )}
                {(proofRecon.severity === "amber" || proofRecon.severity === "red") && onRequestFix && (
                  <Box
                    as="button"
                    px={2}
                    py={0.5}
                    borderRadius="sm"
                    fontSize="10px"
                    fontWeight="700"
                    flexShrink={0}
                    bg="rgba(34,197,94,0.12)"
                    color="#22c55e"
                    border="1px solid rgba(34,197,94,0.3)"
                    cursor="pointer"
                    _hover={{ bg: "rgba(34,197,94,0.2)" }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      onRequestFix(entry)
                    }}
                  >
                    Fix
                  </Box>
                )}
              </HStack>
            </Box>
          )}
        </Box>
      )}

      {/* Proof file list */}
      {files.map((f, i) => (
        <HStack
          key={i}
          gap={3}
          px={3}
          py={1.5}
          borderRadius="md"
          bg="rgba(255,255,255,0.02)"
          cursor="pointer"
          _hover={{ bg: "rgba(255,255,255,0.05)" }}
          onClick={() => openPreview(f.name)}
        >
          <Icon as={FaExternalLinkAlt as React.ElementType} boxSize="10px" color="text.muted" />
          <Text fontSize="xs" flex={1} truncate>{f.name}</Text>
          {f.parsedValue != null && (
            <Text fontSize="xs" fontWeight="600" color="#3b82f6">${f.parsedValue.toFixed(2)}</Text>
          )}
          {f.parsedDate && (
            <Text fontSize="xs" color="text.muted">{formatDate(f.parsedDate)}</Text>
          )}
          {f.parsedSource && (
            <Text fontSize="xs" color="text.muted" fontStyle="italic">{f.parsedSource}</Text>
          )}
        </HStack>
      ))}
    </Stack>
  )
}

// ─── 409A Proof File Parser ──────────────────────────────────────────────────────

function parse409aProofFileName(fileName: string | undefined): { name: string; source: string; date: string } {
  const defaults = { name: "", source: "", date: "" }
  if (!fileName) return defaults

  // Flexible parsing for 409A proof file names. Handles variations like:
  //   "409A at 2026-05-26 is 3.41 per Carta SS FWD Kyung Lee.msg"
  //   "409a at 2025-12-16 is $0.10 per Carta Access from Kevin Coleman(Andrew).png"
  const SEP = /(?:FWD|from|via)\b/i

  const dateMatch = fileName.match(/\bat\s+(\d{4}-\d{2}-\d{2})\b/i)
  const sourceMatch = fileName.match(new RegExp(`\\bper\\s+(.+?)\\s+${SEP.source}`, "i"))
  const nameRaw = fileName.match(new RegExp(`${SEP.source}\\s+(.+?)(?:\\.[a-z]{2,5})?$`, "i"))

  // Strip trailing parenthetical like "(Andrew)" from the name
  const name = nameRaw?.[1]?.trim().replace(/\s*\([^)]*\)\s*$/, "").trim() ?? defaults.name

  return {
    date: dateMatch?.[1] ?? defaults.date,
    source: sourceMatch?.[1]?.trim() ?? defaults.source,
    name,
  }
}

// ─── Proof Reminder Banner ────────────────────────────────────────────────────────

const ProofReminderBanner = ({
  entry,
  dm,
}: {
  entry: ReconcilableEntry
  dm: { id: number; name: string; shortName: string; email: string }
}) => {
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSend = async () => {
    setSending(true)
    try {
      await sendProofReminder({
        dmEmail: dm.email,
        dmName: dm.name,
        companyName: entry.companyName,
        fmvDate: entry.latestFmvDate,
        fmvValue: entry.latestFmv,
        clientName: entry.fmvAddedBy,
      })
      setSent(true)
      toaster.success({ title: "Reminder sent", description: `Email sent to ${dm.name}` })
    } catch {
      toaster.error({ title: "Failed to send reminder" })
    } finally {
      setSending(false)
    }
  }

  return (
    <Box px={3} py={2} borderRadius="md" bg="rgba(239,68,68,0.08)" border="1px solid rgba(239,68,68,0.25)" mt={1}>
      <HStack gap={2} justifyContent="space-between" alignItems="center">
        <Stack gap={0}>
          <Text fontSize="xs" color="#ef4444" fontWeight="600">
            Proof file missing
          </Text>
          <Text fontSize="xs" color="#f87171">
            {entry.fmvAddedBy ? `${entry.fmvAddedBy}'s` : "The"} {formatDate(entry.latestFmvDate)} valuation needs a proof file. {dm.shortName} ({dm.email}) should upload it.
          </Text>
        </Stack>
        <Box
          as="button"
          px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600"
          cursor={sent ? "default" : "pointer"}
          bg={sent ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)"}
          color={sent ? "#22c55e" : "#ef4444"}
          border="1px solid"
          borderColor={sent ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}
          _hover={sent ? {} : { opacity: 0.8 }}
          onClick={sent ? undefined : handleSend}
          flexShrink={0}
        >
          {sending ? <Spinner size="xs" /> : sent ? "Sent" : `Remind ${dm.shortName}`}
        </Box>
      </HStack>
    </Box>
  )
}

// ─── Confirm Modal Body ──────────────────────────────────────────────────────────

const ConfirmModalBody = ({
  action,
  onClose,
  onConfirm,
  existingPunchlistItem,
}: {
  action: ConfirmAction
  onClose: () => void
  onConfirm: (payload?: FixPayload) => void
  existingPunchlistItem?: { suggestedIrrFmv: number; createdAt: string } | null
}) => {
  const { entry, type } = action

  // Load proof files to parse 409A file name defaults
  const { data: proofFiles } = useFmvProofFiles(entry.companyFolderPath)
  const latestProof = proofFiles && proofFiles.length > 0 ? proofFiles[0] : undefined
  const parsed409a = useMemo(() => parse409aProofFileName(latestProof?.name), [latestProof?.name])

  // Compute proof-date-based staleness for smart defaults
  const proofStaleness = latestProof?.parsedDate ? computeStalenessFromDate(latestProof.parsedDate) : null
  const proofFactor = proofStaleness?.factor ?? entry.staleness.factor
  const proofBasedIrr = latestProof?.parsedValue != null
    ? Math.round(latestProof.parsedValue * proofFactor * 100) / 100
    : null

  // Reverse 409A: what 409A value would produce the current IRR FMV?
  const reverse409a = entry.irrFmv != null && entry.staleness.factor > 0
    ? Math.round((entry.irrFmv / entry.staleness.factor) * 100) / 100
    : null

  // Detect when proof file date is newer and changes the discount
  // Dates within 1 day are considered aligned
  const modalDatesAligned = datesWithinDays(entry.latestFmvDate, latestProof?.parsedDate ?? null, 1)
  const proofDateIsNewer = !modalDatesAligned && latestProof?.parsedDate && entry.latestFmvDate
    ? latestProof.parsedDate > entry.latestFmvDate
    : false
  const proofChangesDiscount = proofDateIsNewer && proofStaleness != null
    && Math.abs(proofFactor - entry.staleness.factor) > 0.001

  // Smart initial defaults: if proof changes discount AND IRR already matches, just update the 409A date
  const irrAlreadyMatchesProof = proofChangesDiscount && proofBasedIrr != null && entry.irrFmv != null
    && approxEqual(entry.irrFmv, proofBasedIrr)
  const initialFixOption: FixOption = irrAlreadyMatchesProof
    ? "update_409a"
    : proofChangesDiscount ? "update_both" : "update_irr"
  const initialIrrTarget = proofChangesDiscount && proofBasedIrr != null
    ? proofBasedIrr.toFixed(2)
    : (entry.discountedFmv?.toFixed(2) ?? "")
  const initialFmvTarget = proofChangesDiscount && latestProof?.parsedValue != null
    ? latestProof.parsedValue.toFixed(2)
    : (reverse409a?.toFixed(2) ?? "")

  const [fixOption, setFixOption] = useState<FixOption>(initialFixOption)
  const [irrTarget, setIrrTarget] = useState(initialIrrTarget)
  const [fmvTarget, setFmvTarget] = useState(initialFmvTarget)
  const [fmvName, setFmvName] = useState(parsed409a.name)
  const [fmvComment, setFmvComment] = useState(parsed409a.source)
  const [fmvDate, setFmvDate] = useState(
    irrAlreadyMatchesProof && latestProof?.parsedDate
      ? latestProof.parsedDate
      : (parsed409a.date || entry.latestFmvDate || ""),
  )

  // Update defaults when proof files load asynchronously
  const lastParsedRef = useRef(parsed409a)
  if (parsed409a.name !== lastParsedRef.current.name || parsed409a.source !== lastParsedRef.current.source || parsed409a.date !== lastParsedRef.current.date) {
    lastParsedRef.current = parsed409a
    if (!fmvName && parsed409a.name) setFmvName(parsed409a.name)
    if (!fmvComment && parsed409a.source) setFmvComment(parsed409a.source)
    if ((!fmvDate || fmvDate === entry.latestFmvDate) && parsed409a.date) setFmvDate(parsed409a.date)
  }

  // DB date is newer than proof file — proof-based overrides are irrelevant
  // Dates within 1 day are considered aligned (not "newer")
  const fixDatesAligned = datesWithinDays(entry.latestFmvDate, latestProof?.parsedDate ?? null, 1)
  const dbDateIsNewerThanProof = !fixDatesAligned && latestProof?.parsedDate && entry.latestFmvDate
    ? entry.latestFmvDate >= latestProof.parsedDate
    : false

  // Proof file is missing: DB has a value but no matching proof file exists
  const proofFileMissing = entry.latestFmvDate != null && proofFiles != null && (
    proofFiles.length === 0 || dbDateIsNewerThanProof
  )

  // Smart fix direction: when proof file is newer and changes discount
  const proofDefaultAppliedRef = useRef(false)
  if (!proofDefaultAppliedRef.current && latestProof?.parsedValue != null) {
    proofDefaultAppliedRef.current = true
    if (dbDateIsNewerThanProof) {
      // DB has newer data — keep defaults based on entry.discountedFmv, don't override with proof values
    } else if (proofChangesDiscount && proofBasedIrr != null && entry.irrFmv != null && approxEqual(entry.irrFmv, proofBasedIrr)) {
      // IRR already correct at proof-discounted value → just update the 409A date
      setFixOption("update_409a")
      setFmvTarget(latestProof.parsedValue.toFixed(2))
      if (latestProof.parsedDate) setFmvDate(latestProof.parsedDate)
    } else if (proofChangesDiscount && proofBasedIrr != null) {
      // Proof file has newer date that changes the discount → suggest updating both
      setFixOption("update_both")
      setIrrTarget(proofBasedIrr.toFixed(2))
      setFmvTarget(latestProof.parsedValue.toFixed(2))
    } else if (entry.reconciliation === "irr_mismatch" || entry.reconciliation === "discount_needed") {
      const proofValue = latestProof.parsedValue
      const discountedProof = Math.round(proofValue * proofFactor * 100) / 100
      if (entry.irrFmv != null && approxEqual(entry.irrFmv, discountedProof)) {
        // Proof file agrees with IRR FMV → the 409A is stale → suggest updating 409A
        setFixOption("update_409a")
        setFmvTarget(proofValue.toFixed(2))
      }
    }
  }

  const cancelBtn = (
    <Box
      as="button"
      px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="500"
      border="1px solid" borderColor="border.default" color="text.muted"
      cursor="pointer" _hover={{ bg: "hover.bg" }}
      onClick={onClose}
    >
      Cancel
    </Box>
  )

  // ── Undo ──
  if (type === "undo") {
    return (
      <Stack gap={4}>
        <Text fontSize="sm" color="text.muted">
          Revert {entry.companyName} to its previous state. IRR FMV will be restored to {formatFmv(entry.irrFmv)}.
        </Text>
        <HStack gap={3} pt={2} justifyContent="flex-end">
          {cancelBtn}
          <Box
            as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="600"
            cursor="pointer" bg="rgba(107,114,128,0.15)" color="#9ca3af"
            border="1px solid rgba(107,114,128,0.3)" _hover={{ opacity: 0.9 }}
            onClick={onConfirm}
          >
            Yes, Undo
          </Box>
        </HStack>
      </Stack>
    )
  }

  // ── Mitigate (no_fmv) ──
  if (type === "mitigate") {
    return (
      <Stack gap={4}>
        <Text fontSize="sm" color="text.muted">
          Acknowledge {entry.companyName} has no 409A valuation on file. This flags the entry for follow-up to obtain a current valuation.
        </Text>
        <HStack gap={3} pt={2} justifyContent="flex-end">
          {cancelBtn}
          <Box
            as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="600"
            cursor="pointer" bg="rgba(59,130,246,0.15)" color="#3b82f6"
            border="1px solid rgba(59,130,246,0.3)" _hover={{ opacity: 0.9 }}
            onClick={onConfirm}
          >
            Yes, Mitigate
          </Box>
        </HStack>
      </Stack>
    )
  }

  // ── Execute (irr_mismatch, discount_needed, or no_irr) ──
  const isMismatch = entry.reconciliation === "irr_mismatch" || entry.reconciliation === "discount_needed"

  const optionCard = (
    opt: FixOption,
    label: string,
    children: React.ReactNode,
  ) => {
    const selected = fixOption === opt
    return (
      <Box
        borderRadius="md"
        border="1px solid"
        borderColor={selected ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.08)"}
        bg={selected ? "rgba(34,197,94,0.05)" : "transparent"}
        p={3}
        cursor="pointer"
        onClick={() => setFixOption(opt)}
        _hover={{ borderColor: selected ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.15)" }}
      >
        <HStack gap={2} mb={selected && children ? 2 : 0}>
          <Box
            w="14px" h="14px" borderRadius="full" flexShrink={0}
            border="2px solid"
            borderColor={selected ? "#22c55e" : "rgba(255,255,255,0.2)"}
            display="flex" alignItems="center" justifyContent="center"
          >
            {selected && <Box w="6px" h="6px" borderRadius="full" bg="#22c55e" />}
          </Box>
          <Text fontSize="xs" fontWeight="600">{label}</Text>
        </HStack>
        {selected && children}
      </Box>
    )
  }

  return (
    <Stack gap={4}>
      {/* Current values */}
      <Box borderRadius="md" border="1px solid" borderColor="rgba(255,255,255,0.08)" bg="rgba(255,255,255,0.03)" p={3}>
        <HStack gap={4} flexWrap="wrap">
          <Stack gap={0}>
            <Text fontSize="xs" color="text.muted">409A</Text>
            <Text fontSize="sm" fontWeight="600">{formatFmv(entry.latestFmv)}</Text>
          </Stack>
          <Stack gap={0}>
            <Text fontSize="xs" color="text.muted">IRR FMV</Text>
            <Text fontSize="sm" fontWeight="600">{formatFmv(entry.irrFmv)}</Text>
          </Stack>
          <Stack gap={0}>
            <Text fontSize="xs" color="text.muted">Discount</Text>
            <Text fontSize="sm" fontWeight="600">{(entry.staleness.factor * 100).toFixed(0)}%</Text>
          </Stack>
          {entry.discountedFmv != null && (
            <Stack gap={0}>
              <Text fontSize="xs" color="text.muted">Expected IRR</Text>
              <Text fontSize="sm" fontWeight="600" color="#3b82f6">{formatFmv(entry.discountedFmv)}</Text>
            </Stack>
          )}
        </HStack>
      </Box>

      {/* Fix options */}
      {isMismatch ? (
        <Stack gap={2}>
          <Text fontSize="xs" color="text.muted" fontWeight="600">What should change?</Text>

          {optionCard("update_irr", `Update IRR FMV (currently ${formatFmv(entry.irrFmv)})`, (
            <HStack gap={2} pl={6}>
              <Text fontSize="xs" color="text.muted">New value: $</Text>
              <Input
                size="sm" maxW="100px" variant="outline"
                value={irrTarget}
                onChange={(e) => setIrrTarget(e.target.value)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            </HStack>
          ))}

          {optionCard("update_409a", `Update 409A value (currently ${formatFmv(entry.latestFmv)})`, (
            <Stack gap={2} pl={6}>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Value: $</Text>
                <Input
                  size="sm" maxW="100px" variant="outline"
                  value={fmvTarget}
                  onChange={(e) => setFmvTarget(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Name:</Text>
                <Input
                  size="sm" maxW="200px" variant="outline"
                  value={fmvName}
                  onChange={(e) => setFmvName(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Source:</Text>
                <Input
                  size="sm" maxW="200px" variant="outline"
                  value={fmvComment}
                  onChange={(e) => setFmvComment(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Date:</Text>
                <Input
                  size="sm" maxW="140px" variant="outline" type="date"
                  value={fmvDate}
                  onChange={(e) => setFmvDate(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
            </Stack>
          ))}

          {optionCard("update_both", "Update both values", (
            <Stack gap={2} pl={6}>
              <HStack gap={4}>
                <Stack gap={1}>
                  <Text fontSize="xs" color="text.muted">IRR FMV: $</Text>
                  <Input
                    size="sm" maxW="100px" variant="outline"
                    value={irrTarget}
                    onChange={(e) => setIrrTarget(e.target.value)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                </Stack>
                <Stack gap={1}>
                  <Text fontSize="xs" color="text.muted">409A: $</Text>
                  <Input
                    size="sm" maxW="100px" variant="outline"
                    value={fmvTarget}
                    onChange={(e) => setFmvTarget(e.target.value)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                </Stack>
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Name:</Text>
                <Input
                  size="sm" maxW="200px" variant="outline"
                  value={fmvName}
                  onChange={(e) => setFmvName(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Source:</Text>
                <Input
                  size="sm" maxW="200px" variant="outline"
                  value={fmvComment}
                  onChange={(e) => setFmvComment(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
              <HStack gap={2}>
                <Text fontSize="xs" color="text.muted" minW="55px">Date:</Text>
                <Input
                  size="sm" maxW="140px" variant="outline" type="date"
                  value={fmvDate}
                  onChange={(e) => setFmvDate(e.target.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </HStack>
            </Stack>
          ))}
        </Stack>
      ) : (
        /* no_irr — single option: set IRR FMV */
        <Stack gap={2}>
          <Text fontSize="xs" color="text.muted" fontWeight="600">Set IRR FMV to:</Text>
          <HStack gap={2}>
            <Text fontSize="sm">$</Text>
            <Input
              size="sm" maxW="120px" variant="outline"
              value={irrTarget}
              onChange={(e) => setIrrTarget(e.target.value)}
            />
          </HStack>
        </Stack>
      )}

      {/* Duplicate punchlist warning */}
      {existingPunchlistItem && (fixOption === "update_irr" || fixOption === "update_both") && (
        <Box px={3} py={2} borderRadius="md" bg="rgba(245,158,11,0.1)" border="1px solid rgba(245,158,11,0.25)" mt={1}>
          <Text fontSize="xs" color="#f59e0b">
            This company already has a pending punchlist item (suggested IRR FMV: ${existingPunchlistItem.suggestedIrrFmv.toFixed(2)}).
            Applying will replace the previous entry.
          </Text>
        </Box>
      )}

      {/* Proof file missing — DM reminder */}
      {proofFileMissing && entry.proofUploadDm && (
        <ProofReminderBanner
          entry={entry}
          dm={entry.proofUploadDm}
        />
      )}

      {/* Action buttons */}
      <HStack gap={3} pt={2} justifyContent="flex-end">
        {cancelBtn}
        <Box
          as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="600"
          cursor="pointer" bg="rgba(34,197,94,0.15)" color="#22c55e"
          border="1px solid rgba(34,197,94,0.3)" _hover={{ opacity: 0.9 }}
          onClick={() => onConfirm({
            fixOption,
            irrTarget,
            fmvTarget,
            fmvName,
            fmvComment,
            fmvDate,
            proofFileName: latestProof?.name,
          })}
        >
          Apply
        </Box>
      </HStack>
    </Stack>
  )
}

// ─── DM FMV View Component ──────────────────────────────────────────────────

const DM_COL_WIDTHS = ["30px", "22%", "10%", "10%", "8%", "12%", "15%", "13%"]

const DmColGroup = () => (
  <colgroup>
    {DM_COL_WIDTHS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
)

type DmSortKey = "company" | "fmv" | "date" | "age" | "irrFmv" | "clients" | "status"

const DmFmvView = ({
  entries,
  isLoading: loading,
  isSuperUser,
  managers,
  viewAs,
  setViewAs,
  userId,
  testMode,
  setTestMode,
  stagingPendingCount,
  punchlistPendingCount,
  microsoftConnected,
  microsoftLoading,
  microsoftConnect,
}: {
  entries: DmFmvEntry[]
  isLoading: boolean
  isSuperUser: boolean
  managers: { id: number; name: string; shortName: string; email: string; isFmvReviewer?: boolean }[] | undefined
  viewAs: string | undefined
  setViewAs: (v: string | undefined) => void
  userId?: number
  testMode: boolean
  setTestMode: React.Dispatch<React.SetStateAction<boolean>>
  stagingPendingCount: number
  punchlistPendingCount: number
  microsoftConnected: boolean
  microsoftLoading: boolean
  microsoftConnect: () => void
}) => {
  const queryClient = useQueryClient()
  const [dmActiveTab, setDmActiveTab] = useState<"needsnew" | "reconcile" | "needsproof" | "punchlist" | "staging" | "activity" | "reports">("needsnew")
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<DmSortKey>("age")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Proof status from DB
  const { data: dmProofStatuses } = useProofStatuses(viewAs)
  const { data: dmUnseenCount = 0 } = useFmvActivityUnseenCount(viewAs)

  // Extraction state
  const [extracting, setExtracting] = useState(false)
  const [extractionQueue, setExtractionQueue] = useState<number[]>([])
  const [extractionIndex, setExtractionIndex] = useState(0)
  const [extractionResult, setExtractionResult] = useState<CartaExtractionResult | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmValue, setConfirmValue] = useState("")
  const [confirmDate, setConfirmDate] = useState("")
  const [extractingId, setExtractingId] = useState<number | null>(null)
  const cancelledRef = useRef(false)

  // Outreach state
  const [outreachStatuses, setOutreachStatuses] = useState<Record<number, Record<number, FmvOutreachStatus>>>({})
  const [sendingOutreachKey, setSendingOutreachKey] = useState<string | null>(null)
  const [bulkResult, setBulkResult] = useState<{ sent: number; skipped: number } | null>(null)

  // Email drawer state
  const [drawerClientEmail, setDrawerClientEmail] = useState<string | null>(null)
  const [drawerClientName, setDrawerClientName] = useState("")
  const [drawerCompanyFolderPath, setDrawerCompanyFolderPath] = useState<string | null>(null)
  const [drawerCompanyName, setDrawerCompanyName] = useState("")
  const [drawerCompanyId, setDrawerCompanyId] = useState<number | null>(null)
  const [drawerLatestFmv, setDrawerLatestFmv] = useState<number | null>(null)
  const [drawerLatestFmvDate, setDrawerLatestFmvDate] = useState<string | null>(null)
  const [drawerEmailSearch, setDrawerEmailSearch] = useState("")
  const clientEmailsQuery = useClientEmails(drawerClientEmail)

  // Test mode + compose modal
  const TEST_EMAIL = "esoportaltester@gmail.com"
  const [composeModal, setComposeModal] = useState<{
    mode: "single" | "bulk"
    clientId?: number
    companyId?: number
    toEmail: string
    subject: string
    bodyText: string
    targets?: { clientId: number; companyId: number }[]
    targetCount?: number
  } | null>(null)
  const [composeSending, setComposeSending] = useState(false)

  // FMV fix modal state
  const [fixAction, setFixAction] = useState<ConfirmAction | null>(null)
  const [fixPending, setFixPending] = useState(false)
  const { data: punchlistData } = useQuery({
    queryKey: ["fmvPunchlist"],
    queryFn: getFmvPunchlist,
    staleTime: 5 * 60 * 1000,
  })

  const handleRequestFix = useCallback((entry: ReconcilableEntry) => {
    setFixAction({ entry, type: "execute" })
  }, [])

  const handleFixConfirm = useCallback(async (payload?: FixPayload) => {
    if (!fixAction) return
    const { entry } = fixAction
    if (payload) {
      setFixPending(true)
      try {
        await applyFmvFix(entry.companyId, payload.fixOption, {
          irrTarget: payload.irrTarget ? parseFloat(payload.irrTarget) : undefined,
          fmvValue: payload.fmvTarget ? parseFloat(payload.fmvTarget) : undefined,
          fmvName: payload.fmvName,
          fmvComment: payload.fmvComment,
          fmvDate: payload.fmvDate,
          proofFileName: payload.proofFileName,
        })
        toaster.success({ title: "Fix applied", description: `${entry.companyName} updated` })
        queryClient.invalidateQueries({ queryKey: ["dmFmvReview"] })
        queryClient.invalidateQueries({ queryKey: ["fmvPunchlist"] })
      } catch (err: any) {
        toaster.error({ title: "Fix failed", description: err?.message || "Unknown error" })
        setFixPending(false)
        setFixAction(null)
        return
      }
      setFixPending(false)
    }
    setFixAction(null)
  }, [fixAction, queryClient])

  const handleCorrectDate = useCallback(async (entry: ReconcilableEntry, newDate: string, proofFileName: string) => {
    if (!entry.latestFmvId) {
      toaster.error({ title: "Cannot correct date", description: "No FMV record ID available" })
      return
    }
    try {
      await correctFmvDate(entry.companyId, entry.latestFmvId, newDate, proofFileName)
      toaster.success({ title: "Date corrected", description: `${entry.companyName} FMV date updated to ${newDate}` })
      queryClient.invalidateQueries({ queryKey: ["dmFmvReview"] })
      queryClient.invalidateQueries({ queryKey: ["proofStatuses"] })
    } catch (err: any) {
      toaster.error({ title: "Date correction failed", description: err?.message || "Unknown error" })
    }
  }, [queryClient])

  // Proof file date check — progressively check Dropbox for recent 409A files
  // Companies with a recent proof file (< 1 year old) are excluded from "Needs New FMV"
  const [recentProofCompanyIds, setRecentProofCompanyIds] = useState<Set<number>>(new Set())
  const [proofCheckInProgress, setProofCheckInProgress] = useState(false)
  const proofCheckRanRef = useRef(false)

  useEffect(() => {
    if (proofCheckRanRef.current || loading || entries.length === 0) return
    proofCheckRanRef.current = true

    const companiesNeedingCheck = entries.filter(
      (e) => e.needsNewFmv && e.companyFolderPath
    )
    if (companiesNeedingCheck.length === 0) return

    setProofCheckInProgress(true)
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
    const oneYearAgoStr = oneYearAgo.toISOString().slice(0, 10)

    // Check in batches of 5 to avoid hammering Dropbox
    let cancelled = false
    const recentIds = new Set<number>()
    const BATCH_SIZE = 5

    ;(async () => {
      for (let i = 0; i < companiesNeedingCheck.length && !cancelled; i += BATCH_SIZE) {
        const batch = companiesNeedingCheck.slice(i, i + BATCH_SIZE)
        const results = await Promise.allSettled(
          batch.map(async (entry) => {
            const files = await getFmvProofFiles(entry.companyFolderPath!)
            const recentFile = files.find(
              (f) => f.parsedDate && f.parsedDate >= oneYearAgoStr
            )
            if (recentFile) recentIds.add(entry.companyId)
          })
        )
        // Update state after each batch so the UI updates progressively
        if (!cancelled && recentIds.size > 0) {
          setRecentProofCompanyIds(new Set(recentIds))
        }
      }
      if (!cancelled) {
        setRecentProofCompanyIds(new Set(recentIds))
        setProofCheckInProgress(false)
      }
    })()

    return () => { cancelled = true }
  }, [entries, loading])

  // Fetch outreach statuses when companies are expanded
  useEffect(() => {
    const toFetch = [...expandedIds].filter((id) => !(id in outreachStatuses))
    if (toFetch.length === 0) return
    getOutreachStatuses(toFetch, viewAs).then((data) => {
      setOutreachStatuses((prev) => ({ ...prev, ...data }))
    }).catch(() => {})
  }, [expandedIds]) // eslint-disable-line react-hooks/exhaustive-deps

  // Open compose modal for single email
  const handleSendOutreach = useCallback(async (clientId: number, companyId: number) => {
    // Check Microsoft connection first — show toast if not connected
    if (!microsoftConnected && !microsoftLoading) {
      toaster.error({ title: "Microsoft account not connected", description: "Click 'Connect Outlook' in the header to connect your account" })
      return
    }
    const key = `${clientId}-${companyId}`
    setSendingOutreachKey(key)
    try {
      const preview = await previewFmvOutreach(clientId, companyId, viewAs)
      setComposeModal({
        mode: "single",
        clientId,
        companyId,
        toEmail: testMode ? TEST_EMAIL : preview.toEmail,
        subject: preview.subject,
        bodyText: preview.bodyText,
      })
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || "Unknown error"
      if (errorMsg.toLowerCase().includes("outlook not connected") || errorMsg.toLowerCase().includes("microsoft")) {
        toaster.error({ title: "Microsoft account not connected", description: "Click 'Connect Outlook' in the header to connect your account" })
        return
      }
      toaster.error({ title: "Failed to load preview", description: errorMsg })
    } finally {
      setSendingOutreachKey(null)
    }
  }, [viewAs, testMode, microsoftConnected, microsoftLoading])

  // Compose modal: actually send the email
  const handleComposeSend = useCallback(async () => {
    if (!composeModal) return
    setComposeSending(true)
    try {
      if (composeModal.mode === "single" && composeModal.clientId && composeModal.companyId) {
        const overrides: { toEmail?: string; subject?: string; bodyHtml?: string } = {}
        if (testMode) overrides.toEmail = TEST_EMAIL
        overrides.subject = composeModal.subject
        // Wrap plain text as simple HTML paragraphs
        const escaped = composeModal.bodyText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        const paragraphs = escaped.split(/\n{2,}/).map((p: string) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        overrides.bodyHtml = `<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">${paragraphs.join("\n")}</div>`

        await sendFmvOutreach(composeModal.clientId, composeModal.companyId, viewAs, overrides)
        setOutreachStatuses((prev) => ({
          ...prev,
          [composeModal.companyId!]: {
            ...prev[composeModal.companyId!],
            [composeModal.clientId!]: {
              status: "contacted",
              lastEmailSentAt: new Date().toISOString(),
              replyReceivedAt: null,
              doNotContactNote: null,
            },
          },
        }))
        setComposeModal(null)
        toaster.success({ title: "Email sent", description: testMode ? `Test email sent to ${TEST_EMAIL}` : `Email sent to ${composeModal.toEmail}` })
      } else if (composeModal.mode === "bulk" && composeModal.targets) {
        const templateOverrides: { toEmail?: string; subjectTemplate?: string; bodyTemplate?: string } = {}
        if (testMode) templateOverrides.toEmail = TEST_EMAIL
        templateOverrides.subjectTemplate = composeModal.subject
        templateOverrides.bodyTemplate = composeModal.bodyText

        const result = await sendBulkFmvOutreach(composeModal.targets, viewAs, templateOverrides)
        setBulkResult(result)
        const companyIds = [...new Set(composeModal.targets.map((t) => t.companyId))]
        const freshStatuses = await getOutreachStatuses(companyIds, viewAs)
        setOutreachStatuses((prev) => ({ ...prev, ...freshStatuses }))
        setComposeModal(null)
        toaster.success({
          title: "Bulk email complete",
          description: `${result.sent} sent${result.skipped ? `, ${result.skipped} skipped` : ""}${result.errors.length ? `, ${result.errors.length} errors` : ""}`,
        })
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || "Unknown error"
      // If Outlook isn't connected, show toast and prompt connection
      if (errorMsg.toLowerCase().includes("outlook not connected") || errorMsg.toLowerCase().includes("microsoft")) {
        setComposeModal(null)
        toaster.error({ title: "Microsoft account not connected", description: "Click 'Connect Outlook' to connect your account, then try again" })
        return
      }
      toaster.error({ title: "Send failed", description: errorMsg })
    } finally {
      setComposeSending(false)
    }
  }, [composeModal, viewAs, testMode])

  // Set do-not-contact
  const handleSetDoNotContact = useCallback(async (clientId: number, companyId: number) => {
    const note = prompt("Optional note (why shouldn't we contact this client?):")
    if (note === null) return // cancelled
    try {
      await apiSetDoNotContact(clientId, companyId, note || undefined, viewAs)
      setOutreachStatuses((prev) => ({
        ...prev,
        [companyId]: {
          ...prev[companyId],
          [clientId]: {
            status: "do_not_contact",
            lastEmailSentAt: prev[companyId]?.[clientId]?.lastEmailSentAt || null,
            replyReceivedAt: prev[companyId]?.[clientId]?.replyReceivedAt || null,
            doNotContactNote: note || null,
          },
        },
      }))
    } catch (err: any) {
      alert(`Failed: ${err?.message || "Unknown error"}`)
    }
  }, [viewAs])

  // Clear do-not-contact
  const handleClearDoNotContact = useCallback(async (clientId: number, companyId: number) => {
    try {
      await apiClearDoNotContact(clientId, companyId)
      setOutreachStatuses((prev) => ({
        ...prev,
        [companyId]: {
          ...prev[companyId],
          [clientId]: {
            status: "not_contacted",
            lastEmailSentAt: prev[companyId]?.[clientId]?.lastEmailSentAt || null,
            replyReceivedAt: null,
            doNotContactNote: null,
          },
        },
      }))
    } catch (err: any) {
      alert(`Failed: ${err?.message || "Unknown error"}`)
    }
  }, [])

  // Update DNC note inline
  const handleUpdateDncNote = useCallback(async (clientId: number, companyId: number, note: string) => {
    try {
      await apiSetDoNotContact(clientId, companyId, note || undefined, viewAs)
      setOutreachStatuses((prev) => ({
        ...prev,
        [companyId]: {
          ...prev[companyId],
          [clientId]: {
            ...prev[companyId]?.[clientId],
            status: "do_not_contact" as const,
            lastEmailSentAt: prev[companyId]?.[clientId]?.lastEmailSentAt || null,
            replyReceivedAt: prev[companyId]?.[clientId]?.replyReceivedAt || null,
            doNotContactNote: note || null,
          },
        },
      }))
    } catch (err: any) {
      alert(`Failed: ${err?.message || "Unknown error"}`)
    }
  }, [viewAs])

  // Save DM note for a client
  const handleSaveDmNote = useCallback(async (clientId: number, companyId: number, note: string) => {
    try {
      await apiSaveDmFmvNote(clientId, companyId, note, viewAs)
      // Update React Query cache so icon reacts immediately
      queryClient.setQueriesData<DmFmvEntry[]>({ queryKey: ["dmFmvReview"] }, (prev) =>
        (prev || []).map((e) =>
          e.companyId === companyId
            ? { ...e, closedClients: e.closedClients.map((c) => c.clientId === clientId ? { ...c, dmNote: note || null } : c) }
            : e
        )
      )
    } catch (err: any) {
      alert(`Failed to save note: ${err?.message || "Unknown error"}`)
    }
  }, [viewAs, queryClient])

  // Block all clients for a company at once
  const handleBlockCompany = useCallback(async (companyId: number) => {
    const note = prompt("Optional note (why is this company blocked?):")
    if (note === null) return // cancelled
    try {
      await apiBlockCompany(companyId, note || undefined, viewAs)
      const entry = entries.find((e) => e.companyId === companyId)
      if (entry) {
        setOutreachStatuses((prev) => {
          const companyStatuses = { ...prev[companyId] }
          for (const c of entry.closedClients) {
            companyStatuses[c.clientId] = {
              status: "do_not_contact" as const,
              lastEmailSentAt: prev[companyId]?.[c.clientId]?.lastEmailSentAt ?? null,
              replyReceivedAt: null,
              doNotContactNote: note || null,
            }
          }
          return { ...prev, [companyId]: companyStatuses }
        })
      }
    } catch {
      toaster.create({ title: "Failed to block company", type: "error" })
    }
  }, [entries, viewAs])

  // Unblock all clients for a company
  const handleUnblockCompany = useCallback(async (companyId: number) => {
    try {
      await apiUnblockCompany(companyId, viewAs)
      setOutreachStatuses((prev) => {
        const companyStatuses = { ...prev[companyId] }
        for (const key of Object.keys(companyStatuses)) {
          if (companyStatuses[+key]?.status === "do_not_contact") {
            companyStatuses[+key] = { ...companyStatuses[+key], status: "not_contacted" as const, doNotContactNote: null }
          }
        }
        return { ...prev, [companyId]: companyStatuses }
      })
    } catch {
      toaster.create({ title: "Failed to unblock company", type: "error" })
    }
  }, [viewAs])

  // Mark as contacted (without sending email)
  const handleMarkAsContacted = useCallback(async (clientId: number, companyId: number) => {
    try {
      await apiMarkOutreachStatus(clientId, companyId, "contacted", viewAs)
      setOutreachStatuses((prev) => ({
        ...prev,
        [companyId]: {
          ...prev[companyId],
          [clientId]: {
            status: "contacted",
            lastEmailSentAt: new Date().toISOString(),
            replyReceivedAt: null,
            doNotContactNote: null,
          },
        },
      }))
    } catch (err: any) {
      alert(`Failed: ${err?.message || "Unknown error"}`)
    }
  }, [viewAs])

  const toggleExpand = (companyId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(companyId)) next.delete(companyId)
      else next.add(companyId)
      return next
    })
  }

  const handleSort = (key: DmSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "company" ? "asc" : "desc")
    }
  }

  // Single extraction
  const handleExtract = useCallback(async (cartaAccessId: number) => {
    setExtractingId(cartaAccessId)
    try {
      const result = await cartaExtract(cartaAccessId)
      if (result.status === "ok" || result.success) {
        setExtractionResult(result)
        setConfirmValue(result.value?.toString() || "")
        setConfirmDate(result.date || "")
        setShowConfirmModal(true)
      } else if (result.status === "auth_required") {
        alert("Carta session expired. Please contact Sean to re-authenticate.")
      } else {
        alert(`Extraction failed: ${result.errorMessage || "Unknown error"}`)
      }
    } catch (err: any) {
      alert(`Extraction failed: ${err?.message || "Unknown error"}`)
    } finally {
      setExtractingId(null)
    }
  }, [])

  // Process next in queue
  const processNextExtraction = useCallback(async (currentIdx: number, queue: number[]) => {
    const nextIdx = currentIdx + 1
    if (nextIdx >= queue.length || cancelledRef.current) {
      setExtracting(false)
      setExtractionQueue([])
      setExtractionIndex(0)
      cancelledRef.current = false
      try { await cartaCloseBrowser() } catch { /* ignore */ }
      return
    }
    setExtractionIndex(nextIdx)
    setExtractingId(queue[nextIdx])
    try {
      const result = await cartaExtract(queue[nextIdx])
      if (result.status === "ok" || result.success) {
        setExtractionResult(result)
        setConfirmValue(result.value?.toString() || "")
        setConfirmDate(result.date || "")
        setShowConfirmModal(true)
      } else if (result.status === "auth_required") {
        alert("Carta session expired. Please contact Sean to re-authenticate.")
        setExtracting(false)
        try { await cartaCloseBrowser() } catch { /* ignore */ }
      } else {
        // Skip errors and continue
        processNextExtraction(nextIdx, queue)
      }
    } catch {
      processNextExtraction(nextIdx, queue)
    } finally {
      setExtractingId(null)
    }
  }, [])

  // Confirm extraction
  const handleConfirm = useCallback(async () => {
    if (!extractionResult || !extractionResult.companyId) return
    const value = parseFloat(confirmValue)
    if (isNaN(value) || value <= 0) {
      alert("Please enter a valid FMV value")
      return
    }
    if (!confirmDate) {
      alert("Please enter a valid date")
      return
    }

    try {
      const cartaAccessId = extractingId || extractionQueue[extractionIndex]
      await cartaConfirm({
        cartaAccessId,
        companyId: extractionResult.companyId,
        value,
        date: confirmDate,
        screenshotBase64: extractionResult.screenshotBase64,
        folderPath: extractionResult.folderPath,
        clientCartaName: extractionResult.clientCartaName,
      })
      setShowConfirmModal(false)
      setExtractionResult(null)
      queryClient.invalidateQueries({ queryKey: ["dmFmvReview"] })
      queryClient.invalidateQueries({ queryKey: ["fmvProgress"] })

      if (extracting && !cancelledRef.current) {
        processNextExtraction(extractionIndex, extractionQueue)
      }
    } catch (err: any) {
      alert(`Confirm failed: ${err?.message || "Unknown error"}`)
    }
  }, [extractionResult, confirmValue, confirmDate, extractingId, extractionQueue, extractionIndex, extracting, queryClient, processNextExtraction])

  // Skip current and continue
  const handleSkip = useCallback(() => {
    setShowConfirmModal(false)
    setExtractionResult(null)
    if (extracting && !cancelledRef.current) {
      processNextExtraction(extractionIndex, extractionQueue)
    }
  }, [extracting, extractionIndex, extractionQueue, processNextExtraction])

  // Cancel full extraction
  const handleCancelExtraction = useCallback(async () => {
    cancelledRef.current = true
    setExtracting(false)
    setShowConfirmModal(false)
    setExtractionResult(null)
    setExtractionQueue([])
    setExtractionIndex(0)
    try { await cartaCloseBrowser() } catch { /* ignore */ }
  }, [])

  // Compute stats — exclude companies with recent proof files from the "needs new" count
  const needsNewCount = useMemo(
    () => entries.filter((e) => e.needsNewFmv && !recentProofCompanyIds.has(e.companyId) && !e.isWrittenOff).length,
    [entries, recentProofCompanyIds],
  )
  const reconcileCount = useMemo(
    () => entries.filter((e) => !e.hasPendingPunchlist && e.reconciliation !== "ok" && e.reconciliation !== "stale_match" && e.reconciliation !== "written_off").length,
    [entries],
  )

  // Filter + sort
  const filtered = useMemo(() => {
    let list = entries
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) => e.companyName.toLowerCase().includes(q))
    }
    if (dmActiveTab === "needsnew") {
      list = list.filter((e) => e.needsNewFmv && !recentProofCompanyIds.has(e.companyId) && !e.isWrittenOff)
    }
    if (dmActiveTab === "reconcile") {
      list = list.filter((e) => !e.hasPendingPunchlist && e.reconciliation !== "ok" && e.reconciliation !== "stale_match" && e.reconciliation !== "written_off")
    }
    return [...list].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null
      switch (sortKey) {
        case "company": aVal = a.companyName.toLowerCase(); bVal = b.companyName.toLowerCase(); break
        case "fmv": aVal = a.latestFmv; bVal = b.latestFmv; break
        case "date": aVal = a.latestFmvDate; bVal = b.latestFmvDate; break
        case "age":
          aVal = a.latestFmvDate ? new Date(a.latestFmvDate).getTime() : 0
          bVal = b.latestFmvDate ? new Date(b.latestFmvDate).getTime() : 0
          break
        case "irrFmv": aVal = a.irrFmv; bVal = b.irrFmv; break
        case "clients": aVal = a.closedClients.length; bVal = b.closedClients.length; break
        case "status":
          aVal = 0; bVal = 0; break
        default: aVal = a.companyName.toLowerCase(); bVal = b.companyName.toLowerCase()
      }
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDir === "asc" ? cmp : -cmp
    })
  }, [entries, search, dmActiveTab, sortKey, sortDir, recentProofCompanyIds])

  // Split into unblocked (actionable) and blocked (all clients DNC) sections
  // Use live outreachStatuses when available (updated immediately on DNC toggle),
  // fall back to backend allClientsBlocked for companies not yet expanded
  const { unblockedCompanies, blockedCompanies } = useMemo(() => {
    const unblocked: DmFmvEntry[] = []
    const blocked: DmFmvEntry[] = []
    for (const entry of filtered) {
      const companyStatuses = outreachStatuses[entry.companyId]
      const isBlocked = companyStatuses && entry.closedClients.length > 0
        ? entry.closedClients.every((c) => companyStatuses[c.clientId]?.status === "do_not_contact")
        : entry.allClientsBlocked
      if (isBlocked) blocked.push(entry)
      else unblocked.push(entry)
    }
    return { unblockedCompanies: unblocked, blockedCompanies: blocked }
  }, [filtered, outreachStatuses])

  // ─── Company Row Renderer ───────────────────────────────────────────────────
  const CompanyRow = ({ entry, dimmed }: { entry: DmFmvEntry; dimmed: boolean }) => {
    const isExpanded = expandedIds.has(entry.companyId)
    let yearsStale = 0
    if (entry.latestFmvDate) {
      const diffMs = Date.now() - new Date(entry.latestFmvDate).getTime()
      yearsStale = diffMs / (365.25 * 24 * 60 * 60 * 1000)
    }

    return (
      <>
        <Table.Row
          cursor="pointer"
          _hover={{ bg: "hover.bg" }}
          onClick={() => toggleExpand(entry.companyId)}
          opacity={dimmed ? 0.5 : 1}
        >
          <Table.Cell w="30px" py={3}>
            <Icon as={(isExpanded ? FaChevronDown : FaChevronRight) as React.ElementType} boxSize="10px" color="text.muted" />
          </Table.Cell>
          <Table.Cell py={3}>
            <HStack gap={1.5}>
              {entry.closedClients.some((c) => c.cartaAccessId != null) && (
                <Box as="span" px={1} py={0} borderRadius="3px" fontSize="9px" fontWeight="700"
                  bg="#000" color="#fff" lineHeight="16px" flexShrink={0}>
                  C
                </Box>
              )}
              <Text fontSize="sm" fontWeight="500">{entry.companyName}</Text>
              {entry.closedClients.some((c) => c.dmNote) && (
                <Box as="span" color="#9ca3af" title="Has DM notes">
                  <FaRegStickyNote size={12} />
                </Box>
              )}
            </HStack>
          </Table.Cell>
          <Table.Cell textAlign="right" py={3} title={entry.latestFmvComment || undefined}><Text fontSize="sm" cursor={entry.latestFmvComment ? "help" : undefined}>{formatFmv(entry.latestFmv)}</Text></Table.Cell>
          <Table.Cell py={3}><Text fontSize="sm" color="text.muted">{formatDate(entry.latestFmvDate)}</Text></Table.Cell>
          <Table.Cell textAlign="right" py={3}>
            {entry.latestFmvDate ? (
              <Box as="span" px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600"
                bg={ageBg(yearsStale)} color={ageColor(yearsStale)}>
                {ageLabel(yearsStale)}
              </Box>
            ) : (
              <Text fontSize="sm" color="#6b7280">N/A</Text>
            )}
          </Table.Cell>
          <Table.Cell textAlign="right" py={3}>
            {entry.irrFmv != null ? (
              <HStack gap={1.5} justifyContent="flex-end">
                <Text fontSize="sm">{formatFmv(entry.irrFmv)}</Text>
                {entry.reconciliation !== "ok" && entry.reconciliation !== "stale_match" && (
                  <Box as="span" px={1.5} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="700"
                    bg={reconBadgeStyle(entry.reconciliation).bg}
                    color={reconBadgeStyle(entry.reconciliation).color}>
                    {reconBadgeStyle(entry.reconciliation).label}
                  </Box>
                )}
                {entry.hasPendingPunchlist && (
                  <Box as="span" px={1.5} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="700"
                    bg="rgba(245,158,11,0.15)" color="#f59e0b">
                    QUEUED
                  </Box>
                )}
              </HStack>
            ) : (
              <HStack gap={1.5} justifyContent="flex-end">
                <Text fontSize="sm" color="#6b7280">—</Text>
                {entry.reconciliation === "no_irr" && (
                  <Box as="span" px={1.5} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="700"
                    bg="rgba(107,114,128,0.15)" color="#6b7280">
                    NO IRR
                  </Box>
                )}
              </HStack>
            )}
          </Table.Cell>
          <Table.Cell py={3}><Text fontSize="sm">{entry.closedClients.length} client{entry.closedClients.length !== 1 ? "s" : ""}</Text></Table.Cell>
          <Table.Cell py={3} textAlign="center">
            {dimmed ? (
              <Box as="button" px={2.5} py={1} borderRadius="md" fontSize="10px" fontWeight="600" cursor="pointer"
                color="#6b7280" border="1px solid #374151"
                _hover={{ bg: "rgba(255,255,255,0.05)" }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleUnblockCompany(entry.companyId) }}
              >
                Unblock
              </Box>
            ) : (
              <Box as="button" px={2.5} py={1} borderRadius="md" fontSize="10px" fontWeight="600" cursor="pointer"
                color="#ef4444" bg="rgba(239,68,68,0.06)"
                _hover={{ bg: "rgba(239,68,68,0.12)" }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleBlockCompany(entry.companyId) }}
              >
                Block
              </Box>
            )}
          </Table.Cell>
        </Table.Row>

        {isExpanded && (
          <Table.Row>
            <Table.Cell colSpan={8} p={0} bg="rgba(255,255,255,0.02)">
              <Stack gap={1} pl={8} py={2} pr={4}>
                {/* Select All / Deselect All header */}
                {entry.closedClients.map((client) => {
                  const outreach = outreachStatuses[entry.companyId]?.[client.clientId]
                  const outreachKey = `${client.clientId}-${entry.companyId}`
                  const isSendingThis = sendingOutreachKey === outreachKey
                  const isDnc = outreach?.status === "do_not_contact"
                  const isContacted = outreach?.status === "contacted"
                  const hasReply = isContacted && outreach?.replyReceivedAt != null

                  return (
                    <HStack
                      key={client.clientId}
                      gap={3} px={3} py={2} borderRadius="md"
                      bg="rgba(255,255,255,0.02)" _hover={{ bg: "rgba(255,255,255,0.05)" }}
                    >
                      <Stack gap={0} minW="160px">
                        <Text
                          fontSize="sm" fontWeight="500"
                          cursor={client.clientEmail ? "pointer" : "default"}
                          _hover={client.clientEmail ? { color: "#3b82f6", textDecoration: "underline" } : {}}
                          onClick={(e: React.MouseEvent) => {
                            if (client.clientEmail) {
                              e.stopPropagation()
                              setDrawerClientEmail(client.clientEmail)
                              setDrawerClientName(client.clientName)
                              setDrawerCompanyFolderPath(entry.companyFolderPath)
                              setDrawerCompanyName(entry.companyName)
                              setDrawerCompanyId(entry.companyId)
                              setDrawerLatestFmv(entry.latestFmv)
                              setDrawerLatestFmvDate(entry.latestFmvDate)
                            }
                          }}
                        >
                          {client.clientName}
                        </Text>
                        {isDnc && (
                          <Input
                            size="xs" variant="flushed" fontSize="11px" color="#ef4444"
                            placeholder="Add reason..."
                            defaultValue={outreach?.doNotContactNote || ""}
                            _placeholder={{ color: "rgba(239,68,68,0.4)" }}
                            borderBottomColor="rgba(239,68,68,0.2)"
                            _focus={{ borderBottomColor: "#ef4444" }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                              const val = e.target.value.trim()
                              if (val !== (outreach?.doNotContactNote || "")) {
                                handleUpdateDncNote(client.clientId, entry.companyId, val)
                              }
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === "Enter") (e.target as HTMLInputElement).blur()
                            }}
                          />
                        )}
                        <Input
                          size="xs" variant="flushed" fontSize="11px" color="#6b7280"
                          placeholder="Add note..."
                          defaultValue={client.dmNote || ""}
                          _placeholder={{ color: "rgba(107,114,128,0.4)" }}
                          borderBottomColor="rgba(107,114,128,0.15)"
                          _focus={{ borderBottomColor: "#6b7280" }}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            const val = e.target.value.trim()
                            if (val !== (client.dmNote || "")) {
                              handleSaveDmNote(client.clientId, entry.companyId, val)
                            }
                          }}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur()
                          }}
                        />
                      </Stack>

                      {/* Outreach status badge */}
                      {isDnc && (
                        <Box as="span" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="600"
                          bg="rgba(239,68,68,0.12)" color="#ef4444" flexShrink={0}>
                          DNC
                        </Box>
                      )}
                      {hasReply && (
                        <Box as="span" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="600"
                          bg="rgba(34,197,94,0.12)" color="#22c55e">
                          Reply
                        </Box>
                      )}
                      {isContacted && !hasReply && (
                        <Box as="span" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="600"
                          bg="rgba(245,158,11,0.12)" color="#f59e0b">
                          Contacted
                        </Box>
                      )}

                      {client.fundName && (
                        <Text fontSize="xs" color="text.muted">{client.fundName}</Text>
                      )}
                      {client.totalInvestment != null && (
                        <Text fontSize="xs" color="text.muted">{formatDollars(client.totalInvestment)}</Text>
                      )}
                      {client.closingDate && (
                        <Text fontSize="xs" color="text.muted">Closed {formatDate(client.closingDate)}</Text>
                      )}
                      <Box flex={1} />

                      {/* Do Not Contact toggle */}
                      {isDnc ? (
                        <Box
                          as="button" px={2} py={1} borderRadius="md" fontSize="10px" fontWeight="600" cursor="pointer"
                          color="#6b7280" border="1px solid #e5e7eb"
                          _hover={{ bg: "rgba(0,0,0,0.04)" }}
                          title="Remove do-not-contact flag"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            handleClearDoNotContact(client.clientId, entry.companyId)
                          }}
                        >
                          <HStack gap={1}>
                            <Icon as={FaBan as React.ElementType} boxSize="9px" />
                            <span>Unblock</span>
                          </HStack>
                        </Box>
                      ) : (
                        <Box
                          as="button" px={1.5} py={1} borderRadius="md" cursor="pointer"
                          color="#d1d5db" _hover={{ color: "#ef4444", bg: "rgba(239,68,68,0.06)" }}
                          title="Mark do-not-contact"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            handleSetDoNotContact(client.clientId, entry.companyId)
                          }}
                        >
                          <Icon as={FaBan as React.ElementType} boxSize="11px" />
                        </Box>
                      )}

                      {/* Mark Contacted button — manual override without sending email */}
                      {!isDnc && !hasReply && !isContacted && (
                        <Box
                          as="button"
                          px={2.5} py={1} borderRadius="md" fontSize="10px" fontWeight="600" cursor="pointer"
                          color="#f59e0b" bg="rgba(245,158,11,0.08)" border="1px solid rgba(245,158,11,0.25)"
                          _hover={{ bg: "rgba(245,158,11,0.15)" }}
                          title="Mark as contacted (no email sent)"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            handleMarkAsContacted(client.clientId, entry.companyId)
                          }}
                        >
                          Mark Contacted
                        </Box>
                      )}

                      {/* Email outreach button */}
                      {!isDnc && !hasReply && (
                        <Box
                          as="button"
                          px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
                          bg={isSendingThis ? "rgba(59,130,246,0.15)" : isContacted ? "rgba(245,158,11,0.08)" : "rgba(59,130,246,0.1)"}
                          color={isSendingThis ? "#3b82f6" : isContacted ? "#f59e0b" : "#3b82f6"}
                          border={`1px solid ${isSendingThis ? "rgba(59,130,246,0.3)" : isContacted ? "rgba(245,158,11,0.2)" : "rgba(59,130,246,0.25)"}`}
                          _hover={{ opacity: 0.9 }}
                          opacity={isContacted && !isSendingThis ? 0.6 : 1}
                          title={isContacted ? "Already contacted — click to resend" : "Send FMV request email"}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            if (!isSendingThis) handleSendOutreach(client.clientId, entry.companyId)
                          }}
                        >
                          {isSendingThis ? (
                            <HStack gap={1}><Spinner size="xs" /><span>Sending...</span></HStack>
                          ) : (
                            <HStack gap={1}>
                              <Icon as={FaEnvelope as React.ElementType} boxSize="10px" />
                              <span>{isContacted ? "Resend" : "Email"}</span>
                            </HStack>
                          )}
                        </Box>
                      )}

                      {/* Carta extract button */}
                      {client.cartaAccessId && (
                        <Box
                          as="button"
                          px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
                          bg={extractingId === client.cartaAccessId ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.12)"}
                          color={extractingId === client.cartaAccessId ? "#3b82f6" : "#22c55e"}
                          border={`1px solid ${extractingId === client.cartaAccessId ? "rgba(59,130,246,0.3)" : "rgba(34,197,94,0.3)"}`}
                          _hover={{ opacity: 0.9 }}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            if (extractingId !== client.cartaAccessId) handleExtract(client.cartaAccessId)
                          }}
                        >
                          {extractingId === client.cartaAccessId ? (
                            <HStack gap={1}><Spinner size="xs" /><span>Extracting...</span></HStack>
                          ) : "Extract"}
                        </Box>
                      )}
                    </HStack>
                  )
                })}
                {entry.closedClients.length === 0 && (
                  <Text fontSize="xs" color="text.muted" py={1}>No closed clients found</Text>
                )}
              </Stack>

              {/* Proof files + reconciliation */}
              <ProofFilesRow entry={entry} onRequestFix={handleRequestFix} onCorrectDate={handleCorrectDate} />
            </Table.Cell>
          </Table.Row>
        )}
      </>
    )
  }

  if (loading) {
    return (
      <Section>
        <HStack gap={3} py={10} justifyContent="center">
          <Spinner size="md" />
          <Text>Loading FMV review data...</Text>
        </HStack>
      </Section>
    )
  }

  return (
    <Section h="100vh" overflow="hidden" display="flex" flexDirection="column">
      <Stack gap={4} flex={1} minH={0}>
        {/* Row 1: Title + View As + Test Mode */}
        <HStack gap={3} alignItems="baseline">
          <Text fontSize="2xl" fontWeight="bold">FMV Review</Text>
          {isSuperUser && managers && (
            <Box as="select"
              value={viewAs || ""}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setViewAs(e.target.value || undefined)}
              bg="card.bg" border="1px solid" borderColor="border.default" borderRadius="md"
              px={2} py={1} fontSize="xs" color="text.muted" cursor="pointer"
              _hover={{ borderColor: "#7eb8ff" }}
            >
              <option value="">My View</option>
              <option value="all">All Companies</option>
              {managers.map((m) => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </Box>
          )}
          {isSuperUser && (
            <Box
              as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="700" cursor="pointer" whiteSpace="nowrap"
              bg={testMode ? "rgba(245,158,11,0.15)" : "rgba(0,0,0,0.03)"}
              color={testMode ? "#f59e0b" : "#9ca3af"}
              border={`1px solid ${testMode ? "rgba(245,158,11,0.4)" : "#e5e7eb"}`}
              _hover={{ opacity: 0.85 }}
              onClick={() => setTestMode((p) => !p)}
              title={testMode ? `Test mode ON — emails go to ${TEST_EMAIL}` : "Enable test mode"}
            >
              {testMode ? "Test ON" : "Test"}
            </Box>
          )}
        </HStack>

        {/* Row 2: Tabs (left) + Reports (right) */}
        <HStack gap={0} justifyContent="space-between" alignItems="center">
          <HStack gap={0} bg="rgba(255,255,255,0.05)" borderRadius="md" border="1px solid" borderColor="border.default" overflow="hidden" w="fit-content">
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "needsnew" ? "rgba(239,68,68,0.15)" : "transparent"}
              color={dmActiveTab === "needsnew" ? "#ef4444" : "text.muted"}
              borderRight="1px solid" borderColor="border.default"
              _hover={{ bg: dmActiveTab === "needsnew" ? "rgba(239,68,68,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("needsnew")}
            >
              Needs New FMV
              {needsNewCount > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
                  {needsNewCount}
                </Box>
              )}
            </Box>
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "reconcile" ? "rgba(168,85,247,0.15)" : "transparent"}
              color={dmActiveTab === "reconcile" ? "#a855f7" : "text.muted"}
              borderRight="1px solid" borderColor="border.default"
              _hover={{ bg: dmActiveTab === "reconcile" ? "rgba(168,85,247,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("reconcile")}
            >
              Reconcile
              {reconcileCount > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(168,85,247,0.2)" color="#a855f7" lineHeight="1">
                  {reconcileCount}
                </Box>
              )}
            </Box>
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "needsproof" ? "rgba(59,130,246,0.15)" : "transparent"}
              color={dmActiveTab === "needsproof" ? "#3b82f6" : "text.muted"}
              borderRight="1px solid" borderColor="border.default"
              _hover={{ bg: dmActiveTab === "needsproof" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("needsproof")}
            >
              Needs Proof
              {(dmProofStatuses?.length ?? 0) > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
                  {dmProofStatuses?.length ?? 0}
                </Box>
              )}
            </Box>
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "staging" ? "rgba(59,130,246,0.15)" : "transparent"}
              color={dmActiveTab === "staging" ? "#3b82f6" : "text.muted"}
              borderRight="1px solid" borderColor="border.default"
              _hover={{ bg: dmActiveTab === "staging" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("staging")}
            >
              FMV Staging
              {stagingPendingCount > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(245,158,11,0.2)" color="#f59e0b" lineHeight="1">
                  {stagingPendingCount}
                </Box>
              )}
            </Box>
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "punchlist" ? "rgba(59,130,246,0.15)" : "transparent"}
              color={dmActiveTab === "punchlist" ? "#3b82f6" : "text.muted"}
              borderRight="1px solid" borderColor="border.default"
              _hover={{ bg: dmActiveTab === "punchlist" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("punchlist")}
            >
              Punchlist
              {punchlistPendingCount > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
                  {punchlistPendingCount}
                </Box>
              )}
            </Box>
            <Box
              as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
              bg={dmActiveTab === "activity" ? "rgba(139,92,246,0.15)" : "transparent"}
              color={dmActiveTab === "activity" ? "#8b5cf6" : "text.muted"}
              _hover={{ bg: dmActiveTab === "activity" ? "rgba(139,92,246,0.2)" : "hover.bg" }}
              onClick={() => setDmActiveTab("activity")}
            >
              Activity
              {dmUnseenCount > 0 && (
                <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                  bg="rgba(139,92,246,0.2)" color="#8b5cf6" lineHeight="1">
                  {dmUnseenCount}
                </Box>
              )}
            </Box>
          </HStack>
          <Box
            as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
            bg={dmActiveTab === "reports" ? "rgba(20,184,166,0.15)" : "transparent"}
            color={dmActiveTab === "reports" ? "#14b8a6" : "text.muted"}
            borderRadius="md" border="1px solid"
            borderColor={dmActiveTab === "reports" ? "rgba(20,184,166,0.4)" : "border.default"}
            _hover={{ bg: dmActiveTab === "reports" ? "rgba(20,184,166,0.2)" : "hover.bg" }}
            onClick={() => setDmActiveTab("reports")}
          >
            Reports
          </Box>
        </HStack>

        {(dmActiveTab === "needsnew" || dmActiveTab === "reconcile") && (<>
        {/* Row 3: Search + count */}
        <HStack gap={3} justifyContent="space-between" alignItems="center">
          <HStack gap={3} alignItems="center" flexShrink={0}>
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              w="240px"
              size="sm"
            />
            <Text fontSize="sm" color="text.muted" whiteSpace="nowrap">{filtered.length} companies</Text>
          </HStack>
        </HStack>

        {/* Contextual description based on active tab */}
        <Text fontSize="xs" color="text.muted" lineHeight="1.5">
          {dmActiveTab === "needsnew"
            ? "These companies need an updated 409A value. Contact the client or check Carta to get the latest fair market value, then use the Carta extract button or email the client directly."
            : "These companies have a 409A value that doesn't match the IRR Calculator. Review the proof files, verify the correct value, and apply a fix to update the records."}
        </Text>

        {/* Fixed header */}
        <Table.Root size="md" variant="outline" style={{ tableLayout: "fixed" }}>
          <DmColGroup />
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="30px" />
              {([
                ["Company", "company", undefined],
                ["Current FMV", "fmv", "right"],
                ["Date", "date", undefined],
                ["Age", "age", "right"],
                ["IRR FMV", "irrFmv", "right"],
                ["Clients", "clients", undefined],
                ["", "status", undefined],
              ] as [string, DmSortKey, "right" | undefined][]).map(([label, key, align]) => (
                <Table.ColumnHeader
                  key={key}
                  textAlign={align}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                  _hover={{ color: "white" }}
                  userSelect="none"
                >
                  <HStack gap={1} justifyContent={align === "right" ? "flex-end" : "flex-start"}>
                    <Text fontSize="sm">{label}</Text>
                    {sortKey === key && <Icon as={(sortDir === "asc" ? FaSortUp : FaSortDown) as React.ElementType} boxSize="10px" color="white" />}
                  </HStack>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
        </Table.Root>

        {/* Scrollable body */}
        <Box ref={tableScrollRef} flex={1} minH={0} overflowY="auto">
          <Table.Root size="md" variant="outline" style={{ tableLayout: "fixed", borderTop: "none" }}>
            <DmColGroup />
            <Table.Body>
              {unblockedCompanies.map((entry) => <CompanyRow key={entry.companyId} entry={entry} dimmed={false} />)}

              {blockedCompanies.length > 0 && (
                <Table.Row>
                  <Table.Cell colSpan={8} py={2} px={0}>
                    <HStack gap={2} px={4} py={1.5}>
                      <Box h="1px" flex={1} bg="rgba(239,68,68,0.15)" />
                      <Text fontSize="xs" fontWeight="600" color="#6b7280" letterSpacing="0.05em">
                        BLOCKED ({blockedCompanies.length})
                      </Text>
                      <Box h="1px" flex={1} bg="rgba(239,68,68,0.15)" />
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              )}

              {blockedCompanies.map((entry) => <CompanyRow key={entry.companyId} entry={entry} dimmed={true} />)}
            </Table.Body>
          </Table.Root>

          {unblockedCompanies.length === 0 && blockedCompanies.length === 0 && (
            <Text fontSize="sm" color="text.muted" textAlign="center" py={8}>
              {search ? "No companies match the current search" : "No companies found"}
            </Text>
          )}
        </Box>
        </>)}

        {dmActiveTab === "punchlist" && <FmvPunchlistTab />}
        {dmActiveTab === "needsproof" && (
          <Box flex={1} minH={0} overflowY="auto">
            <NeedsProofTab
              viewAs={viewAs}
              onOpenEmailDrawer={(email, name, folderPath, companyName, companyId, fmv) => { setDrawerClientEmail(email); setDrawerClientName(name); setDrawerCompanyFolderPath(folderPath); setDrawerCompanyName(companyName); setDrawerCompanyId(companyId); setDrawerLatestFmv(fmv?.value ?? null); setDrawerLatestFmvDate(fmv?.date ?? null) }}
            />
          </Box>
        )}
        {dmActiveTab === "staging" && <Box flex={1} minH={0} overflowY="auto"><FmvStagingTab viewAs={viewAs} isReviewer={false} testMode={testMode} /></Box>}
        {dmActiveTab === "activity" && <Box flex={1} minH={0} overflowY="auto"><FmvActivityTab viewAs={viewAs} /></Box>}
        {dmActiveTab === "reports" && <Box flex={1} minH={0} overflowY="auto"><FmvChangeReportTab /></Box>}
      </Stack>

      {/* Scroll to top */}
      <IconButton
        aria-label="Scroll to top" size="sm" position="fixed" bottom="20px" right="20px"
        onClick={() => tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
        borderRadius="full" opacity={0.7} _hover={{ opacity: 1 }}
      >
        <FaArrowUp />
      </IconButton>

      {/* FMV Fix Modal */}
      <Dialog.Root open={!!fixAction} onOpenChange={(e) => { if (!e.open) setFixAction(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="700px">
              <Dialog.Header px={6} pt={5} pb={3}>
                <Dialog.Title fontFamily="body" fontWeight="semibold">
                  Fix: {fixAction?.entry.companyName}
                </Dialog.Title>
                <CloseButton size="sm" position="absolute" top={3} right={3} onClick={() => setFixAction(null)} />
              </Dialog.Header>
              <Dialog.Body px={6} pb={6}>
                {fixAction && (
                  <ConfirmModalBody
                    action={fixAction}
                    onClose={() => setFixAction(null)}
                    onConfirm={handleFixConfirm}
                    existingPunchlistItem={punchlistData?.pending?.find(
                      (p: any) => p.company?.id === fixAction.entry.companyId
                    )}
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Extraction Confirmation Modal */}
      <Dialog.Root open={showConfirmModal} onOpenChange={(e) => { if (!e.open) handleSkip() }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="700px">
              <Dialog.Header px={6} pt={5} pb={3}>
                <Dialog.Title fontFamily="body" fontWeight="semibold">
                  Confirm Carta Extraction: {extractionResult?.companyName}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body px={6} pb={6}>
                {extractionResult && (
                  <Stack gap={4}>
                    {extractionResult.screenshotBase64 && (
                      <Box borderRadius="md" border="1px solid" borderColor="rgba(255,255,255,0.08)" overflow="hidden" maxH="400px" overflowY="auto">
                        <Image
                          src={`data:image/png;base64,${extractionResult.screenshotBase64}`}
                          alt="Carta screenshot" w="100%"
                        />
                      </Box>
                    )}

                    <Box borderRadius="md" border="1px solid" borderColor="rgba(255,255,255,0.08)" bg="rgba(255,255,255,0.03)" p={3}>
                      <HStack gap={4} flexWrap="wrap">
                        <Stack gap={0}>
                          <Text fontSize="xs" color="text.muted">Company</Text>
                          <Text fontSize="sm" fontWeight="600">{extractionResult.companyName}</Text>
                        </Stack>
                        <Stack gap={0}>
                          <Text fontSize="xs" color="text.muted">Client</Text>
                          <Text fontSize="sm" fontWeight="600">{extractionResult.clientCartaName}</Text>
                        </Stack>
                      </HStack>
                    </Box>

                    <HStack gap={4}>
                      <Stack gap={1} flex={1}>
                        <Text fontSize="xs" color="text.muted" fontWeight="600">FMV Value ($)</Text>
                        <Input size="sm" value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)} type="number" step="0.01" />
                      </Stack>
                      <Stack gap={1} flex={1}>
                        <Text fontSize="xs" color="text.muted" fontWeight="600">FMV Date</Text>
                        <Input size="sm" value={confirmDate} onChange={(e) => setConfirmDate(e.target.value)} type="date" />
                      </Stack>
                    </HStack>

                    <HStack gap={3} pt={2} justifyContent="flex-end">
                      {extracting && (
                        <Box as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="500"
                          border="1px solid" borderColor="rgba(239,68,68,0.3)" color="#ef4444"
                          cursor="pointer" _hover={{ bg: "rgba(239,68,68,0.1)" }}
                          onClick={handleCancelExtraction}>
                          Cancel All
                        </Box>
                      )}
                      <Box as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="500"
                        border="1px solid" borderColor="border.default" color="text.muted"
                        cursor="pointer" _hover={{ bg: "hover.bg" }}
                        onClick={handleSkip}>
                        Skip
                      </Box>
                      <Box as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="600"
                        cursor="pointer" bg="rgba(34,197,94,0.15)" color="#22c55e"
                        border="1px solid rgba(34,197,94,0.3)" _hover={{ opacity: 0.9 }}
                        onClick={handleConfirm}>
                        Confirm & Save
                      </Box>
                    </HStack>
                  </Stack>
                )}
              </Dialog.Body>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Compose Email Modal */}
      <Dialog.Root open={composeModal !== null} onOpenChange={(e) => { if (!e.open) setComposeModal(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="white" maxW="600px" color="#1f2937">
              <Dialog.Header px={6} pt={5} pb={3}>
                <Dialog.Title fontFamily="body" fontWeight="semibold" color="#111827">
                  {composeModal?.mode === "bulk" ? "Compose Bulk Email" : "Compose Email"}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body px={6} pb={6}>
                {composeModal && (
                  <Stack gap={4}>
                    {/* Test mode banner */}
                    {testMode && (
                      <Box px={3} py={2} borderRadius="md" bg="rgba(245,158,11,0.1)" border="1px solid rgba(245,158,11,0.3)">
                        <Text fontSize="xs" fontWeight="600" color="#f59e0b">
                          TEST MODE — all emails will be sent to {TEST_EMAIL}
                        </Text>
                      </Box>
                    )}

                    {/* Bulk personalization note */}
                    {composeModal.mode === "bulk" && (
                      <Box px={3} py={2} borderRadius="md" bg="rgba(59,130,246,0.06)" border="1px solid rgba(59,130,246,0.2)">
                        <Text fontSize="xs" color="#3b82f6" fontWeight="500">
                          Sending to <strong>{composeModal.targetCount}</strong> client{composeModal.targetCount !== 1 ? "s" : ""}. Each email will be personalized — {"{{clientFirstName}}"}, {"{{companyName}}"}, and {"{{dmName}}"} will be replaced automatically.
                        </Text>
                      </Box>
                    )}

                    {/* To field */}
                    <Stack gap={1}>
                      <Text fontSize="xs" fontWeight="600" color="#6b7280">To</Text>
                      <Input
                        size="sm" value={composeModal.toEmail} readOnly
                        bg="#f9fafb" border="1px solid #e5e7eb" color="#374151"
                        _focus={{ borderColor: "#e5e7eb" }}
                      />
                    </Stack>

                    {/* Subject field */}
                    <Stack gap={1}>
                      <Text fontSize="xs" fontWeight="600" color="#6b7280">Subject</Text>
                      <Input
                        size="sm"
                        value={composeModal.subject}
                        onChange={(e) => setComposeModal((prev) => prev ? { ...prev, subject: e.target.value } : null)}
                        bg="white" border="1px solid #d1d5db" color="#111827"
                        _focus={{ borderColor: "#3b82f6" }}
                      />
                    </Stack>

                    {/* Body field */}
                    <Stack gap={1}>
                      <Text fontSize="xs" fontWeight="600" color="#6b7280">Body</Text>
                      <Textarea
                        size="sm" rows={8}
                        value={composeModal.bodyText}
                        onChange={(e) => setComposeModal((prev) => prev ? { ...prev, bodyText: e.target.value } : null)}
                        bg="white" border="1px solid #d1d5db" color="#111827"
                        fontFamily="Arial, sans-serif" fontSize="13px" lineHeight="1.6"
                        _focus={{ borderColor: "#3b82f6" }}
                      />
                    </Stack>

                    {/* Actions */}
                    <HStack gap={3} pt={2} justifyContent="flex-end">
                      <Box as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="500"
                        border="1px solid #e5e7eb" color="#6b7280"
                        cursor="pointer" _hover={{ bg: "#f3f4f6" }}
                        onClick={() => setComposeModal(null)}>
                        Cancel
                      </Box>
                      <Box as="button" px={4} py={2} borderRadius="md" fontSize="sm" fontWeight="600"
                        cursor="pointer" bg={composeSending ? "rgba(59,130,246,0.15)" : "#3b82f6"} color="white"
                        _hover={{ opacity: 0.9 }}
                        opacity={composeSending ? 0.7 : 1}
                        onClick={() => { if (!composeSending) handleComposeSend() }}>
                        <HStack gap={1.5}>
                          {composeSending ? <Spinner size="xs" /> : <Icon as={FaEnvelope as React.ElementType} boxSize="11px" />}
                          <span>{composeSending ? "Sending..." : composeModal.mode === "bulk" ? `Send to ${composeModal.targetCount} Clients` : "Send"}</span>
                        </HStack>
                      </Box>
                    </HStack>
                  </Stack>
                )}
              </Dialog.Body>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Bulk Send Result */}
      {bulkResult && (
        <Box
          position="fixed" bottom="60px" right="20px" zIndex={1000}
          bg="card.bg" border="1px solid" borderColor="border.default" borderRadius="md"
          px={4} py={3} boxShadow="lg" maxW="300px"
        >
          <HStack justifyContent="space-between" mb={1}>
            <Text fontSize="sm" fontWeight="600">Emails Sent</Text>
            <Box as="button" cursor="pointer" color="text.muted" _hover={{ color: "white" }}
              onClick={() => setBulkResult(null)}>
              <Icon as={FaTimes as React.ElementType} boxSize="10px" />
            </Box>
          </HStack>
          <Text fontSize="xs" color="text.muted">
            {bulkResult.sent} sent, {bulkResult.skipped} skipped
          </Text>
        </Box>
      )}

      {/* Client Email Drawer */}
      <Drawer.Root
        open={!!drawerClientEmail}
        onOpenChange={(e) => { if (!e.open) { setDrawerClientEmail(null); setDrawerCompanyFolderPath(null); setDrawerCompanyName(""); setDrawerCompanyId(null); setDrawerLatestFmv(null); setDrawerLatestFmvDate(null); setDrawerEmailSearch("") } }}
        placement="end" size="lg"
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header borderBottomWidth="1px">
                <Drawer.Title fontSize="md">Emails — {drawerClientName}</Drawer.Title>
                <Drawer.CloseTrigger />
              </Drawer.Header>
              <Drawer.Body px={4} py={3}>
                {clientEmailsQuery.isLoading ? (
                  <HStack gap={3} py={10} justifyContent="center">
                    <Spinner size="md" />
                    <Text fontSize="sm" color="text.muted">Loading emails...</Text>
                  </HStack>
                ) : clientEmailsQuery.data && !clientEmailsQuery.data.microsoftConnected ? (
                  <Stack gap={3} py={10} alignItems="center">
                    <Text fontSize="sm" color="text.muted">Microsoft account not connected.</Text>
                    <Text fontSize="xs" color="text.muted">Connect your Outlook account from the Deal Page to view emails.</Text>
                  </Stack>
                ) : clientEmailsQuery.data?.emails.length === 0 ? (
                  <Text fontSize="sm" color="text.muted" py={10} textAlign="center">No emails found with this client.</Text>
                ) : (() => {
                  const allEmails = clientEmailsQuery.data?.emails || []
                  const term = drawerEmailSearch.toLowerCase().trim()
                  const filtered = term
                    ? allEmails.filter((e) =>
                        (e.subject || "").toLowerCase().includes(term) ||
                        e.bodyPreview.toLowerCase().includes(term) ||
                        (e.fromName || "").toLowerCase().includes(term) ||
                        e.fromAddress.toLowerCase().includes(term)
                      )
                    : allEmails
                  return (
                    <Stack gap={2}>
                      <Input
                        size="sm" placeholder="Search emails..." value={drawerEmailSearch}
                        onChange={(e) => setDrawerEmailSearch(e.target.value)}
                      />
                      {term && (
                        <Text fontSize="xs" color="text.muted">{filtered.length} of {allEmails.length} emails</Text>
                      )}
                      {filtered.map((email) => (
                        <EmailCard key={email.id} email={email} companyFolderPath={drawerCompanyFolderPath} companyName={drawerCompanyName} companyId={drawerCompanyId} latestFmv={drawerLatestFmv} latestFmvDate={drawerLatestFmvDate} />
                      ))}
                    </Stack>
                  )
                })()}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </Section>
  )
}

/** Email card for the client drawer — click to open full email modal with attachments */
const EmailCard = ({ email, companyFolderPath, companyName, companyId, latestFmv, latestFmvDate }: {
  email: import("@/types/company").ClientEmail
  companyFolderPath?: string | null
  companyName?: string
  companyId?: number | null
  latestFmv?: number | null
  latestFmvDate?: string | null
}) => {
  const [open, setOpen] = useState(false)
  const [stagingInProgress, setStagingInProgress] = useState(false)
  const queryClient = useQueryClient()
  const isFromClient = !email.fromAddress.toLowerCase().includes("esofund.com")
  const attachmentsQuery = useClientEmailAttachments(open && email.hasAttachments ? email.id : null)

  // Build proof file prefix: 409A at {date} is ${value} per {source} FROM {sender}
  // source defaults to "SS" (screenshot); sender is the email author
  const proofFilePrefix = useMemo(() => {
    if (latestFmv == null && !latestFmvDate) return null
    const parts = ["409A"]
    if (latestFmvDate) parts.push(`at ${latestFmvDate}`)
    if (latestFmv != null) parts.push(`is $${latestFmv}`)
    parts.push("per SS")
    const sender = email.fromName || email.fromAddress
    if (sender) parts.push(`FROM ${sender}`)
    return parts.join(" ")
  }, [latestFmv, latestFmvDate, email.fromName, email.fromAddress])

  // Adapt ClientEmail → DealEmail shape for the shared EmailDetailModal
  const dealEmail: DealEmail | null = useMemo(() => ({
    id: 0,
    graphMessageId: email.id,
    conversationId: null,
    subject: email.subject,
    bodyPreview: email.bodyPreview,
    bodyContent: email.bodyContent,
    bodyContentType: email.bodyContentType,
    fromAddress: email.fromAddress,
    fromName: email.fromName,
    toAddresses: email.toAddresses,
    ccAddresses: [],
    receivedAt: email.receivedAt,
    isRead: true,
    hasAttachments: email.hasAttachments,
    importance: null,
  }), [email])

  const handleStageAs409A = async (att: { name: string; contentType: string; contentBytes: string }) => {
    if (!companyId) return
    // Pre-open window synchronously (before await) to avoid popup blocker
    const win = window.open("", "_blank")
    setStagingInProgress(true)
    try {
      const result = await stageAttachmentAs409A({
        companyId,
        companyName: companyName || null,
        folderPath: companyFolderPath || null,
        contentBytes: att.contentBytes,
        fileName: att.name,
        mimeType: att.contentType,
        clientName: email.fromName || email.fromAddress || null,
      })
      queryClient.invalidateQueries({ queryKey: ["fmvStagingList"] })
      const valueStr = result.fmvValue != null ? ` — $${result.fmvValue}` : ""
      const dateStr = result.fmvDate ? ` (${result.fmvDate})` : ""
      toaster.success({ title: "Staged as 409A", description: `${att.name}${valueStr}${dateStr}` })
      if (result.stagingId && win) {
        win.location.href = `/core/fmv_staging/${result.stagingId}`
      } else if (win) {
        win.close()
      }
    } catch {
      toaster.error({ title: "Failed to stage attachment" })
      if (win) win.close()
    } finally {
      setStagingInProgress(false)
    }
  }

  const handleUploadEmail = async (target: "company" | "deal", fileName: string, overrideFolderPath?: string) => {
    const folderPath = target === "deal" ? undefined : (overrideFolderPath || companyFolderPath)
    if (!folderPath) return
    await uploadEmailToDropbox({
      messageId: email.id,
      folderPath,
      fileName,
      companyName,
    })
  }

  const handleStageEmailAs409A = async () => {
    if (!companyId) return
    // Pre-open window synchronously (before await) to avoid popup blocker
    const win = window.open("", "_blank")
    setStagingInProgress(true)
    try {
      const result = await stageEmailAs409A({
        messageId: email.id,
        companyId,
        companyName: companyName || null,
        folderPath: companyFolderPath || null,
        clientName: email.fromName || email.fromAddress || null,
        subject: email.subject || null,
        senderEmail: email.fromAddress || null,
      })
      queryClient.invalidateQueries({ queryKey: ["fmvStagingList"] })
      const valueStr = result.fmvValue != null ? ` — $${result.fmvValue}` : ""
      const dateStr = result.fmvDate ? ` (${result.fmvDate})` : ""
      toaster.success({ title: "Staged email as 409A", description: `Email${valueStr}${dateStr}` })
      if (result.stagingId && win) {
        win.location.href = `/core/fmv_staging/${result.stagingId}`
      } else if (win) {
        win.close()
      }
    } catch {
      toaster.error({ title: "Failed to stage email" })
      if (win) win.close()
    } finally {
      setStagingInProgress(false)
    }
  }

  return (
    <>
      <Box
        borderRadius="md" border="1px solid" borderColor="border.default"
        bg={isFromClient ? "rgba(59,130,246,0.04)" : "rgba(255,255,255,0.02)"}
        overflow="hidden" cursor="pointer" _hover={{ bg: "rgba(255,255,255,0.04)" }}
        onClick={() => setOpen(true)}
        px={3} py={2.5}
      >
        <HStack justifyContent="space-between" alignItems="flex-start">
          <Stack gap={0.5} flex={1} minW={0}>
            <HStack gap={2}>
              <Text fontSize="xs" fontWeight="600" color={isFromClient ? "#3b82f6" : "#22c55e"}>
                {isFromClient ? "Client" : "ESO"}
              </Text>
              <Text fontSize="xs" color="text.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {email.fromName || email.fromAddress}
              </Text>
            </HStack>
            <Text fontSize="sm" fontWeight="500" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {email.subject || "(no subject)"}
            </Text>
            <Text fontSize="xs" color="text.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {email.bodyPreview}
            </Text>
          </Stack>
          <Stack gap={0} alignItems="flex-end" flexShrink={0}>
            <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">
              {formatDate(email.receivedAt)}
            </Text>
            {email.hasAttachments && (
              <Text fontSize="2xs" color="text.muted">📎 Attachment</Text>
            )}
          </Stack>
        </HStack>
      </Box>

      <EmailDetailModal
        email={dealEmail}
        open={open}
        onClose={() => setOpen(false)}
        companyFolderPath={companyFolderPath}
        companyName={companyName}
        externalAttachments={{ data: attachmentsQuery.data || [], isLoading: attachmentsQuery.isLoading }}
        onStageAs409A={companyId ? handleStageAs409A : undefined}
        stagingInProgress={stagingInProgress}
        proofFilePrefix={proofFilePrefix}
        onUploadEmail={companyFolderPath ? handleUploadEmail : undefined}
        onStageEmailAs409A={companyId ? handleStageEmailAs409A : undefined}
      />
    </>
  )
}

// ─── Needs Proof Tab ──────────────────────────────────────────────────────────

const NEEDS_PROOF_COL_WIDTHS = ["180px", "120px", "90px", "100px", "100px", "100px", "140px"]
const NeedsProofColGroup = () => (
  <colgroup>
    {NEEDS_PROOF_COL_WIDTHS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
)

type ProofSortKey = "company" | "client" | "fmv" | "fmvDate" | "proofDate" | "dm"

const NeedsProofTab = ({
  viewAs,
  onOpenEmailDrawer,
}: {
  viewAs?: string
  onOpenEmailDrawer: (email: string, name: string, companyFolderPath: string | null, companyName: string, companyId: number, fmv?: { value: number | null; date: string | null }) => void
}) => {
  const queryClient = useQueryClient()
  const { data: entries, isLoading } = useProofStatuses(viewAs)
  const [syncing, setSyncing] = useState(false)
  const [sortKey, setSortKey] = useState<ProofSortKey>("company")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")

  const handleSort = (key: ProofSortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const sorted = useMemo(() => {
    if (!entries || entries.length === 0) return []
    const arr = [...entries]
    const dir = sortDir === "asc" ? 1 : -1
    arr.sort((a, b) => {
      switch (sortKey) {
        case "company": return dir * a.companyName.localeCompare(b.companyName)
        case "client": return dir * (a.fmvAddedBy || "").localeCompare(b.fmvAddedBy || "")
        case "fmv": return dir * ((a.latestFmv ?? 0) - (b.latestFmv ?? 0))
        case "fmvDate": return dir * (a.latestFmvDate || "").localeCompare(b.latestFmvDate || "")
        case "proofDate": return dir * (a.latestProofDate || "").localeCompare(b.latestProofDate || "")
        case "dm": return dir * a.proofUploadDm.shortName.localeCompare(b.proofUploadDm.shortName)
        default: return 0
      }
    })
    return arr
  }, [entries, sortKey, sortDir])

  const handleSync = async () => {
    setSyncing(true)
    try {
      const result = await syncProofStatuses()
      queryClient.invalidateQueries({ queryKey: ["proofStatuses"] })
      toaster.success({ title: "Sync complete", description: `${result.missing} companies need proof (${result.skipped} skipped due to API errors)` })
    } catch {
      toaster.error({ title: "Sync failed" })
    } finally {
      setSyncing(false)
    }
  }

  // Auto-sync on first mount if no data exists
  const hasAutoSynced = useRef(false)
  useEffect(() => {
    if (!isLoading && entries && entries.length === 0 && !hasAutoSynced.current) {
      hasAutoSynced.current = true
      handleSync()
    }
  }, [isLoading, entries])

  if (isLoading) {
    return (
      <Box py={10} textAlign="center">
        <HStack gap={2} justifyContent="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color="text.muted">Loading proof statuses...</Text>
        </HStack>
      </Box>
    )
  }

  const proofSortHeader = (label: string, key: ProofSortKey) => (
    <Table.ColumnHeader
      cursor="pointer"
      onClick={() => handleSort(key)}
      _hover={{ color: "white" }}
      userSelect="none"
    >
      <HStack gap={1}>
        <Text fontSize="sm">{label}</Text>
        {sortKey === key && <Icon as={(sortDir === "asc" ? FaSortUp : FaSortDown) as React.ElementType} boxSize="10px" color="white" />}
      </HStack>
    </Table.ColumnHeader>
  )

  return (
    <Box flex={1} minH={0} display="flex" flexDirection="column">
      <HStack justifyContent="space-between" px={2} py={2} flexShrink={0}>
        <Text fontSize="xs" color="text.muted">
          {entries && entries.length > 0
            ? `${entries.length} companies missing proof files`
            : "No companies with missing proof files"}
          {entries && entries.length > 0 && entries[0]?.checkedAt && (
            <> &middot; Last synced {formatRelativeTime(entries[0].checkedAt)}</>
          )}
        </Text>
        <Box
          as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
          bg="rgba(59,130,246,0.1)" color="#3b82f6"
          _hover={{ opacity: 0.8 }}
          onClick={handleSync}
          disabled={syncing}
          opacity={syncing ? 0.5 : 1}
        >
          {syncing ? (
            <HStack gap={1.5}>
              <Spinner size="xs" />
              <Text fontSize="xs">Syncing...</Text>
            </HStack>
          ) : "Sync All"}
        </Box>
      </HStack>

      <Text fontSize="xs" color="text.muted" px={2} lineHeight="1.5">
        These companies have a recent 409A value in the database but no matching proof file in Dropbox. Upload the proof document (Carta screenshot, valuation report, or client email) to the company's folder to resolve each item.
      </Text>

      {entries && entries.length > 0 && (<>
        {/* Fixed header */}
        <Table.Root size="sm" variant="outline" style={{ tableLayout: "fixed" }}>
          <NeedsProofColGroup />
          <Table.Header>
            <Table.Row>
              {proofSortHeader("Company", "company")}
              {proofSortHeader("Client", "client")}
              {proofSortHeader("Current FMV", "fmv")}
              {proofSortHeader("409A Date", "fmvDate")}
              {proofSortHeader("Latest Proof", "proofDate")}
              {proofSortHeader("Responsible DM", "dm")}
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
        </Table.Root>

        {/* Scrollable body */}
        <Box flex={1} minH={0} overflowY="auto">
          <Table.Root size="sm" variant="outline" style={{ tableLayout: "fixed", borderTop: "none" }}>
            <NeedsProofColGroup />
            <Table.Body>
              {sorted.map((entry) => (
                <NeedsProofRow key={entry.companyId} entry={entry} onOpenEmailDrawer={onOpenEmailDrawer} />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </>)}
    </Box>
  )
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const NeedsProofRow = ({
  entry,
  onOpenEmailDrawer,
}: {
  entry: ProofStatusEntry
  onOpenEmailDrawer: (email: string, name: string, companyFolderPath: string | null, companyName: string, companyId: number, fmv?: { value: number | null; date: string | null }) => void
}) => {
  const queryClient = useQueryClient()
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [rechecking, setRechecking] = useState(false)

  const handleSendReminder = async () => {
    setSending(true)
    try {
      await sendProofReminder({
        dmEmail: entry.proofUploadDm.email,
        dmName: entry.proofUploadDm.name,
        companyName: entry.companyName,
        fmvDate: entry.latestFmvDate,
        fmvValue: entry.latestFmv,
        clientName: entry.fmvAddedBy,
      })
      setSent(true)
      toaster.success({ title: "Reminder sent", description: `Email sent to ${entry.proofUploadDm.name}` })
    } catch {
      toaster.error({ title: "Failed to send reminder" })
    } finally {
      setSending(false)
    }
  }

  const handleRecheck = async () => {
    setRechecking(true)
    try {
      const result = await recheckProofStatus(entry.companyId)
      queryClient.invalidateQueries({ queryKey: ["proofStatuses"] })
      if (result.proofFound) {
        toaster.success({ title: "Proof found", description: `${entry.companyName} has been resolved` })
      } else if (result.error) {
        toaster.error({ title: "Recheck failed", description: result.error })
      } else {
        toaster.error({ title: "Still missing", description: `No matching proof file found for ${entry.companyName}` })
      }
    } catch {
      toaster.error({ title: "Recheck failed" })
    } finally {
      setRechecking(false)
    }
  }

  return (
    <Table.Row _hover={{ bg: "hover.bg" }}>
      <Table.Cell>
        <Text
          as="a"
          href={`/company/${entry.companyId}`}
          target="_blank"
          fontSize="sm" fontWeight="500" truncate
          color="#3b82f6" cursor="pointer"
          _hover={{ textDecoration: "underline" }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {entry.companyName}
        </Text>
      </Table.Cell>
      <Table.Cell>
        {entry.fmvAddedBy ? (
          entry.fmvAddedByEmail ? (
            <Text
              fontSize="sm" color="#3b82f6" cursor="pointer" truncate
              _hover={{ textDecoration: "underline" }}
              onClick={() => onOpenEmailDrawer(entry.fmvAddedByEmail!, entry.fmvAddedBy!, entry.companyFolderPath, entry.companyName, entry.companyId, { value: entry.latestFmv, date: entry.latestFmvDate })}
            >
              {entry.fmvAddedBy}
            </Text>
          ) : (
            <Text fontSize="sm" color="text.muted" truncate>{entry.fmvAddedBy}</Text>
          )
        ) : (
          <Text fontSize="sm" color="text.muted">—</Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">{entry.latestFmv != null ? formatFmv(entry.latestFmv) : "—"}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">{formatDate(entry.latestFmvDate)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm" color="text.muted">{entry.latestProofDate ? formatDate(entry.latestProofDate) : "None"}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">{entry.proofUploadDm.shortName}</Text>
      </Table.Cell>
      <Table.Cell>
        <HStack gap={1}>
          <Box
            as="button" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
            bg="rgba(59,130,246,0.1)" color="#3b82f6"
            _hover={{ opacity: 0.8 }}
            onClick={handleRecheck}
            disabled={rechecking}
            opacity={rechecking ? 0.5 : 1}
          >
            {rechecking ? <Spinner size="xs" /> : "Recheck"}
          </Box>
          <Box
            as="button"
            px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
            bg={sent ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.1)"}
            color={sent ? "#22c55e" : "#3b82f6"}
            _hover={{ opacity: 0.8 }}
            onClick={handleSendReminder}
            disabled={sending || sent}
            opacity={sending ? 0.5 : 1}
          >
            {sent ? "Sent" : sending ? "..." : "Remind"}
          </Box>
        </HStack>
      </Table.Cell>
    </Table.Row>
  )
}

// ─── Activity Tab ──────────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  carta_confirm: { label: "Carta", color: "#3b82f6" },
  email_attachment_upload: { label: "Upload", color: "#10b981" },
  email_upload: { label: "Upload", color: "#10b981" },
  fmv_fix_applied: { label: "Fix", color: "#f59e0b" },
  punchlist_completed: { label: "Punchlist", color: "#8b5cf6" },
  punchlist_reopened: { label: "Punchlist", color: "#6b7280" },
  dm_note_saved: { label: "Note", color: "#6366f1" },
  staging_confirmed: { label: "Staging", color: "#14b8a6" },
  outreach_sent: { label: "Email", color: "#ec4899" },
  proof_reminder_sent: { label: "Reminder", color: "#f97316" },
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const ActivityRow = ({ item, isNew }: { item: FmvActivityItem; isNew?: boolean }) => {
  const info = ACTION_LABELS[item.action] || { label: item.action, color: "#6b7280" }
  return (
    <HStack
      gap={3} px={3} py={2} borderRadius="md"
      bg={isNew ? "rgba(139,92,246,0.06)" : "transparent"}
      borderLeft={isNew ? "3px solid #8b5cf6" : "3px solid transparent"}
    >
      <Box
        px={1.5} py={0.5} borderRadius="sm" fontSize="2xs" fontWeight="700"
        bg={`${info.color}22`} color={info.color} whiteSpace="nowrap" lineHeight="1.2"
      >
        {info.label}
      </Box>
      <Text fontSize="xs" fontWeight="600" whiteSpace="nowrap">{item.companyName}</Text>
      <Text fontSize="xs" color="text.muted" flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {item.summary}
      </Text>
      <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">{item.actorName}</Text>
      <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">{formatTimeAgo(item.createdAt)}</Text>
    </HStack>
  )
}

const FmvActivityTab = ({ viewAs }: { viewAs?: string }) => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useFmvActivity(viewAs)

  // Mark as seen on mount
  useEffect(() => {
    apiMarkFmvActivitySeen().then(() => {
      queryClient.invalidateQueries({ queryKey: ["fmvActivityUnseenCount"] })
    })
  }, [queryClient])

  if (isLoading) {
    return <HStack gap={3} py={10} justifyContent="center"><Spinner size="md" /><Text fontSize="sm" color="text.muted">Loading activity...</Text></HStack>
  }

  const items = data?.items || []
  const lastSeenAt = data?.lastSeenAt ? new Date(data.lastSeenAt) : null

  const newItems = lastSeenAt
    ? items.filter((i) => new Date(i.createdAt) > lastSeenAt)
    : items
  const earlierItems = lastSeenAt
    ? items.filter((i) => new Date(i.createdAt) <= lastSeenAt)
    : []

  return (
    <Stack gap={1} p={2}>
      {newItems.length > 0 && (
        <>
          <Text fontSize="xs" fontWeight="700" color="#8b5cf6" px={2} pt={1}>New ({newItems.length})</Text>
          {newItems.map((item) => <ActivityRow key={item.id} item={item} isNew />)}
        </>
      )}
      {earlierItems.length > 0 && (
        <>
          <Text fontSize="xs" fontWeight="700" color="text.muted" px={2} pt={newItems.length > 0 ? 3 : 1}>Earlier</Text>
          {earlierItems.map((item) => <ActivityRow key={item.id} item={item} />)}
        </>
      )}
      {items.length === 0 && (
        <Text fontSize="sm" color="text.muted" textAlign="center" py={8}>No activity yet.</Text>
      )}
    </Stack>
  )
}

// ─── Punchlist Tab ──────────────────────────────────────────────────────────────

const PunchlistColGroup = ({ isPending }: { isPending: boolean }) => (
  <colgroup>
    <col style={{ width: isPending ? "22%" : "18%" }} />
    <col style={{ width: "13%" }} />
    <col style={{ width: "11%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: isPending ? "20%" : "16%" }} />
    <col style={{ width: "8%" }} />
    <col style={{ width: "8%" }} />
    {!isPending && <col style={{ width: "8%" }} />}
    <col style={{ width: isPending ? "8%" : "8%" }} />
  </colgroup>
)

const FmvPunchlistTab = () => {
  const queryClient = useQueryClient()
  const [subTab, setSubTab] = useState<"pending" | "completed">("pending")
  const [sortKey, setSortKey] = useState<"company" | "created">("created")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")
  const { data, isLoading } = useQuery({
    queryKey: ["fmvPunchlist"],
    queryFn: getFmvPunchlist,
  })

  const completeMutation = useMutation({
    mutationFn: completePunchlistItemApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fmvPunchlist"] })
      toaster.success({ title: "Marked as done" })
    },
    onError: () => toaster.error({ title: "Failed to update item" }),
  })

  const reopenMutation = useMutation({
    mutationFn: reopenPunchlistItemApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fmvPunchlist"] })
      toaster.success({ title: "Item reopened" })
    },
    onError: () => toaster.error({ title: "Failed to reopen item" }),
  })

  const resyncMutation = useMutation({
    mutationFn: resyncIrrFmv,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["fmvPunchlist"] })
      queryClient.invalidateQueries({ queryKey: ["fmvReview"] })
      queryClient.invalidateQueries({ queryKey: ["fmvProgress"] })
      toaster.success({ title: "IRR resync complete", description: `Updated ${result.updated} companies from ${result.total} legacy records` })
    },
    onError: () => toaster.error({ title: "Resync failed" }),
  })

  const toggleSort = (key: "company" | "created") => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir(key === "company" ? "asc" : "desc") }
  }

  if (isLoading) {
    return (
      <HStack gap={3} py={10} justifyContent="center">
        <Spinner size="md" />
        <Text>Loading punchlist...</Text>
      </HStack>
    )
  }

  const pending = data?.pending || []
  const completed = data?.completed || []
  const isPending = subTab === "pending"
  const unsorted = isPending ? pending : completed
  const items = [...unsorted].sort((a, b) => {
    const mul = sortDir === "asc" ? 1 : -1
    if (sortKey === "company") return mul * a.company.name.localeCompare(b.company.name)
    return mul * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  })

  const formatDate = (d: string | null) => {
    if (!d) return "—"
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  const copyCell = (text: string) => {
    navigator.clipboard.writeText(text)
    toaster.success({ title: "Copied", description: text, duration: 1500 })
  }

  const sortArrow = (key: "company" | "created") =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""

  return (
    <Box display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
      <Text fontSize="xs" color="text.muted" px={4} pt={2} flexShrink={0}>
        Items below need to be manually entered into the IRR Calculator on SharePoint. After updating, click "Done" to mark each item as complete.
      </Text>
      {/* Sub-tab bar + resync button */}
      <HStack justifyContent="space-between" alignItems="center" px={4} py={2} flexShrink={0}>
        <HStack gap={1}>
          <Box
            as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
            bg={subTab === "pending" ? "rgba(59,130,246,0.15)" : "transparent"}
            color={subTab === "pending" ? "#3b82f6" : "text.muted"}
            _hover={{ bg: subTab === "pending" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
            onClick={() => setSubTab("pending")}
          >
            Pending{pending.length > 0 && ` (${pending.length})`}
          </Box>
          <Box
            as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
            bg={subTab === "completed" ? "rgba(59,130,246,0.15)" : "transparent"}
            color={subTab === "completed" ? "#3b82f6" : "text.muted"}
            _hover={{ bg: subTab === "completed" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
            onClick={() => setSubTab("completed")}
          >
            Completed{completed.length > 0 && ` (${completed.length})`}
          </Box>
        </HStack>
        <Box
          as="button" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600"
          cursor="pointer" bg="rgba(59,130,246,0.12)" color="#3b82f6"
          border="1px solid rgba(59,130,246,0.3)" _hover={{ opacity: 0.9 }}
          flexShrink={0} whiteSpace="nowrap"
          onClick={() => resyncMutation.mutate()}
        >
          {resyncMutation.isPending ? <><Spinner size="xs" mr={1} /> Syncing...</> : "Resync IRR"}
        </Box>
      </HStack>

      {/* Fixed header */}
      <Table.Root size="sm" variant="outline" style={{ tableLayout: "fixed" }}>
        <PunchlistColGroup isPending={isPending} />
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader cursor="pointer" _hover={{ color: "#3b82f6" }} onClick={() => toggleSort("company")}>Company{sortArrow("company")}</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Suggested IRR FMV</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Current IRR FMV</Table.ColumnHeader>
            <Table.ColumnHeader>409A Date</Table.ColumnHeader>
            <Table.ColumnHeader>Proof File</Table.ColumnHeader>
            <Table.ColumnHeader>By</Table.ColumnHeader>
            <Table.ColumnHeader cursor="pointer" _hover={{ color: "#3b82f6" }} onClick={() => toggleSort("created")}>Created{sortArrow("created")}</Table.ColumnHeader>
            {!isPending && <Table.ColumnHeader>Done By</Table.ColumnHeader>}
            <Table.ColumnHeader textAlign="center">Action</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
      </Table.Root>

      {/* Scrollable body */}
      <Box flex={1} minH={0} overflowY="auto">
        {items.length === 0 ? (
          <Text fontSize="sm" color="text.muted" textAlign="center" py={8}>
            {isPending ? "No pending items. All IRR Calculator updates are done." : "No recently completed items."}
          </Text>
        ) : (
          <Table.Root size="sm" variant="outline" style={{ tableLayout: "fixed", borderTop: "none" }}>
            <PunchlistColGroup isPending={isPending} />
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.id} bg={isPending ? undefined : "rgba(34,197,94,0.05)"}>
                  <Table.Cell fontWeight="500" cursor="pointer" _hover={{ color: "#3b82f6" }} onClick={() => copyCell(item.company.name)}>{item.company.name}</Table.Cell>
                  <Table.Cell textAlign="right" fontWeight="600" color="#3b82f6" cursor="pointer" _hover={{ opacity: 0.7 }} onClick={() => copyCell(item.suggestedIrrFmv.toFixed(2))}>
                    ${item.suggestedIrrFmv.toFixed(2)}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {item.current409aValue != null ? `$${item.current409aValue.toFixed(2)}` : "—"}
                  </Table.Cell>
                  <Table.Cell fontSize="xs">{formatDate(item.current409aDate)}</Table.Cell>
                  <Table.Cell fontSize="xs" title={item.proofFileName || undefined} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap"
                    cursor={item.proofFileName ? "pointer" : undefined} _hover={item.proofFileName ? { color: "#3b82f6" } : undefined}
                    onClick={() => item.proofFileName && copyCell(item.proofFileName)}>
                    {item.proofFileName || "—"}
                  </Table.Cell>
                  <Table.Cell fontSize="xs" color="text.muted">{item.createdBy?.shortName || "—"}</Table.Cell>
                  <Table.Cell fontSize="xs" color="text.muted">{formatDate(item.createdAt)}</Table.Cell>
                  {!isPending && <Table.Cell fontSize="xs" color="text.muted">{item.completedBy?.shortName || "—"}</Table.Cell>}
                  <Table.Cell textAlign="center">
                    {isPending ? (
                      <Box
                        as="button" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="600"
                        cursor="pointer" bg="rgba(34,197,94,0.15)" color="#22c55e"
                        border="1px solid rgba(34,197,94,0.3)" _hover={{ opacity: 0.9 }}
                        onClick={() => completeMutation.mutate(item.id)}
                      >
                        Done
                      </Box>
                    ) : (
                      <Box
                        as="button" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="500"
                        cursor="pointer" bg="transparent" color="text.muted"
                        border="1px solid" borderColor="border.default" _hover={{ bg: "hover.bg" }}
                        onClick={() => reopenMutation.mutate(item.id)}
                      >
                        Reopen
                      </Box>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>
    </Box>
  )
}

// ─── FMV Staging Tab ────────────────────────────────────────────────────────────

const ICON_AIRCRAFT = "ICON Aircraft"

const FmvStagingTab = ({ viewAs, isReviewer, testMode }: { viewAs?: string; isReviewer: boolean; testMode: boolean }) => {
  const { data: rawRecords = [], isLoading } = useFmvStagingList(viewAs)
  const records = testMode ? rawRecords : rawRecords.filter((r) => r.companyName !== ICON_AIRCRAFT)

  if (isLoading) {
    return (
      <HStack gap={3} py={10} justifyContent="center">
        <Spinner size="md" />
        <Text>Loading staging records...</Text>
      </HStack>
    )
  }

  if (records.length === 0) {
    return (
      <Box py={10} textAlign="center">
        <Text color="text.muted">No pending FMV staging records</Text>
      </Box>
    )
  }

  return (
    <Box overflowX="auto" flex={1}>
      <Table.Root size="md" variant="outline" style={{ tableLayout: "fixed" }}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader w="200px">Company</Table.ColumnHeader>
            <Table.ColumnHeader w="160px">From</Table.ColumnHeader>
            <Table.ColumnHeader w="100px">FMV</Table.ColumnHeader>
            <Table.ColumnHeader w="110px">Date</Table.ColumnHeader>
            <Table.ColumnHeader w="120px">Source</Table.ColumnHeader>
            {isReviewer && <Table.ColumnHeader w="80px">DM</Table.ColumnHeader>}
            <Table.ColumnHeader w="100px">Received</Table.ColumnHeader>
            <Table.ColumnHeader w="80px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {records.map((r) => (
            <Table.Row
              key={r.id}
              cursor="pointer"
              _hover={{ bg: "hover.bg" }}
              onClick={() => window.open(`/core/fmv_staging/${r.id}`, "_blank")}
            >
              <Table.Cell fontWeight="600" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {r.companyName || "Unknown"}
              </Table.Cell>
              <Table.Cell overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {r.senderName || r.senderEmail || "—"}
              </Table.Cell>
              <Table.Cell>
                {r.fmvValue != null ? `$${r.fmvValue.toFixed(2)}` : <Text as="span" color="text.muted">—</Text>}
              </Table.Cell>
              <Table.Cell>{r.fmvDate || "—"}</Table.Cell>
              <Table.Cell overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {r.fmvSource || "—"}
              </Table.Cell>
              {isReviewer && <Table.Cell>{r.dmShortName || "—"}</Table.Cell>}
              <Table.Cell fontSize="xs" color="text.muted">
                {new Date(r.createdAt).toLocaleDateString()}
              </Table.Cell>
              <Table.Cell>
                <Text fontSize="xs" color="#3b82f6" fontWeight="600">Review →</Text>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

// ─── FMV Change Report Tab ──────────────────────────────────────────────────────

const FmvChangeReportTab = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const threeMonthsAgo = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 3)
    return d.toISOString().slice(0, 10)
  }, [])

  const [startDate, setStartDate] = useState(threeMonthsAgo)
  const [endDate, setEndDate] = useState(today)
  const [submittedStart, setSubmittedStart] = useState(threeMonthsAgo)
  const [submittedEnd, setSubmittedEnd] = useState(today)
  const [subTab, setSubTab] = useState<"decrease" | "increase" | "nochange">("decrease")

  const hasChanges = startDate !== submittedStart || endDate !== submittedEnd

  const { data, isLoading } = useFmvChangeReport(submittedStart, submittedEnd)

  const handleSubmit = () => {
    setSubmittedStart(startDate)
    setSubmittedEnd(endDate)
  }

  const { decreases, increases, noChanges } = useMemo(() => {
    const entries = data?.entries || []
    const dec: typeof entries = []
    const inc: typeof entries = []
    const nc: typeof entries = []
    for (const e of entries) {
      if (e.percentChange == null) continue
      if (e.percentChange < 0) dec.push(e)
      else if (e.percentChange > 0) inc.push(e)
      else nc.push(e)
    }
    dec.sort((a, b) => Math.abs(b.percentChange!) - Math.abs(a.percentChange!))
    inc.sort((a, b) => Math.abs(b.percentChange!) - Math.abs(a.percentChange!))
    nc.sort((a, b) => a.companyName.localeCompare(b.companyName))
    return { decreases: dec, increases: inc, noChanges: nc }
  }, [data])

  const activeEntries = subTab === "decrease" ? decreases : subTab === "increase" ? increases : noChanges

  return (
    <Stack gap={3} flex={1} minH={0}>
      {/* Date inputs + Submit */}
      <HStack gap={3} alignItems="center">
        <Box>
          <Text fontSize="2xs" color="text.muted" mb={0.5}>Start Date</Text>
          <Input
            type="date" value={startDate} size="sm" w="160px"
            bg="card.bg" border="1px solid" borderColor="border.default" borderRadius="md" fontSize="xs"
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Box>
        <Text fontSize="sm" color="text.muted" mt={4}>to</Text>
        <Box>
          <Text fontSize="2xs" color="text.muted" mb={0.5}>End Date</Text>
          <Input
            type="date" value={endDate} size="sm" w="160px"
            bg="card.bg" border="1px solid" borderColor="border.default" borderRadius="md" fontSize="xs"
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Box>
        {hasChanges && (
          <Box
            as="button" px={4} py={1.5} mt={4} fontSize="xs" fontWeight="600" cursor="pointer"
            bg="rgba(59,130,246,0.15)" color="#3b82f6" borderRadius="md"
            border="1px solid" borderColor="rgba(59,130,246,0.3)"
            _hover={{ bg: "rgba(59,130,246,0.25)" }}
            onClick={handleSubmit}
          >
            Submit
          </Box>
        )}
        {isLoading && <Spinner size="sm" mt={4} />}
      </HStack>

      {/* Sub-tabs: Decrease | Increase | No Change */}
      <HStack gap={0} bg="rgba(255,255,255,0.05)" borderRadius="md" border="1px solid" borderColor="border.default" overflow="hidden" w="fit-content">
        <Box
          as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
          bg={subTab === "decrease" ? "rgba(239,68,68,0.15)" : "transparent"}
          color={subTab === "decrease" ? "#ef4444" : "text.muted"}
          borderRight="1px solid" borderColor="border.default"
          _hover={{ bg: subTab === "decrease" ? "rgba(239,68,68,0.2)" : "hover.bg" }}
          onClick={() => setSubTab("decrease")}
        >
          Decrease
          {decreases.length > 0 && (
            <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
              bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
              {decreases.length}
            </Box>
          )}
        </Box>
        <Box
          as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
          bg={subTab === "increase" ? "rgba(34,197,94,0.15)" : "transparent"}
          color={subTab === "increase" ? "#22c55e" : "text.muted"}
          borderRight="1px solid" borderColor="border.default"
          _hover={{ bg: subTab === "increase" ? "rgba(34,197,94,0.2)" : "hover.bg" }}
          onClick={() => setSubTab("increase")}
        >
          Increase
          {increases.length > 0 && (
            <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
              bg="rgba(34,197,94,0.2)" color="#22c55e" lineHeight="1">
              {increases.length}
            </Box>
          )}
        </Box>
        <Box
          as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
          bg={subTab === "nochange" ? "rgba(107,114,128,0.15)" : "transparent"}
          color={subTab === "nochange" ? "#6b7280" : "text.muted"}
          _hover={{ bg: subTab === "nochange" ? "rgba(107,114,128,0.2)" : "hover.bg" }}
          onClick={() => setSubTab("nochange")}
        >
          No Change
          {noChanges.length > 0 && (
            <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
              bg="rgba(107,114,128,0.2)" color="#6b7280" lineHeight="1">
              {noChanges.length}
            </Box>
          )}
        </Box>
      </HStack>

      {/* Table */}
      <Box flex={1} minH={0} overflowY="auto">
        <Table.Root size="sm" stickyHeader>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader fontSize="xs" color="text.muted">Company</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted" textAlign="right">Start FMV</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted">Start Date</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted" textAlign="right">End FMV</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted">End Date</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted" textAlign="right">% Change</Table.ColumnHeader>
              <Table.ColumnHeader fontSize="xs" color="text.muted" textAlign="right">Total Invested</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {activeEntries.map((e) => (
              <Table.Row key={e.companyId}>
                <Table.Cell fontSize="xs" fontWeight="500">{e.companyName}</Table.Cell>
                <Table.Cell fontSize="xs" textAlign="right">{e.startFmv != null ? `$${e.startFmv.toFixed(2)}` : "—"}</Table.Cell>
                <Table.Cell fontSize="xs" color="text.muted">{e.startFmvDate || "—"}</Table.Cell>
                <Table.Cell fontSize="xs" textAlign="right">{e.endFmv != null ? `$${e.endFmv.toFixed(2)}` : "—"}</Table.Cell>
                <Table.Cell fontSize="xs" color="text.muted">{e.endFmvDate || "—"}</Table.Cell>
                <Table.Cell fontSize="xs" textAlign="right" fontWeight="600"
                  color={e.percentChange != null && e.percentChange < 0 ? "#ef4444" : e.percentChange != null && e.percentChange > 0 ? "#22c55e" : "text.muted"}
                >
                  {e.percentChange != null ? `${e.percentChange > 0 ? "+" : ""}${e.percentChange.toFixed(2)}%` : "—"}
                </Table.Cell>
                <Table.Cell fontSize="xs" textAlign="right" color="text.muted">{formatDollars(e.totalInvested)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        {activeEntries.length === 0 && !isLoading && (
          <Text fontSize="sm" color="text.muted" textAlign="center" py={8}>
            {subTab === "decrease" ? "No companies with FMV decreases in this period"
              : subTab === "increase" ? "No companies with FMV increases in this period"
              : "No companies with unchanged FMV in this period"}
          </Text>
        )}
      </Box>
    </Stack>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export const FmvReviewComponent = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const dm = user as DealManager | null
  const userRoles = dm?.roleAssignments?.map((ra) => ra.role.name) || []
  const GOD_MODE_EMAILS = ["sean@esofund.com", "maryjane@esofund.com"]
  const isSuperUser = GOD_MODE_EMAILS.includes(dm?.email ?? "")

  const [viewAs, setViewAs] = useState<string | undefined>(undefined)
  const [testMode, setTestMode] = useState(false)
  const { data: managers } = useFmvManagers(isSuperUser)

  // When viewing as a specific person, check if they're a DM or a reviewer
  const isViewingAsDm = useMemo(() => {
    if (!viewAs || viewAs === "all" || !managers) return false
    const person = managers.find((m) => String(m.id) === viewAs)
    if (!person) return false
    // Use the explicit isFmvReviewer flag from backend
    return !person.isFmvReviewer
  }, [viewAs, managers])
  const isReviewer = !isViewingAsDm && (isSuperUser || userRoles.includes("fmv_reviewer"))

  // Microsoft connection — used by DM view for email outreach
  const { isConnected: microsoftConnected, isLoading: microsoftLoading, connect: microsoftConnect } = useMicrosoftConnection()

  // If the user is a regular DM (not admin/reviewer), they always see the DM view
  const isDmView = !isReviewer

  const { data: entries, isLoading } = useFmvReview(viewAs)
  const { data: dmEntries, isLoading: isDmLoading } = useDmFmvReview(
    isDmView ? (viewAs || String(dm?.id)) : undefined,
    testMode,
  )
  const [search, setSearch] = useState("")
  const [filterStale, setFilterStale] = useState(false)
  const [filterReconcile, setFilterReconcile] = useState(false)
  const [filterPunchlist, setFilterPunchlist] = useState(false)
  const [filterTrouble, setFilterTrouble] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>("company")
  const [sortDir, setSortDir] = useState<SortDir>("asc")
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [actedIds, setActedIds] = useState<Set<number>>(new Set())
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [fixPending, setFixPending] = useState(false)
  const [activeTab, setActiveTab] = useState<"review" | "needsproof" | "staging" | "punchlist" | "activity" | "reports">("review")
  const { data: reviewerUnseenCount = 0 } = useFmvActivityUnseenCount()
  const { data: punchlistData } = useQuery({
    queryKey: ["fmvPunchlist"],
    queryFn: getFmvPunchlist,
    staleTime: 5 * 60 * 1000,
  })
  const punchlistPendingCount = punchlistData?.pending?.length ?? 0
  const { data: stagingRecords = [] } = useFmvStagingList(viewAs)
  const stagingPendingCount = testMode ? stagingRecords.length : stagingRecords.filter((r) => r.companyName !== ICON_AIRCRAFT).length
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Email drawer state (for Needs Proof tab)
  const [reviewerDrawerEmail, setReviewerDrawerEmail] = useState<string | null>(null)
  const [reviewerDrawerName, setReviewerDrawerName] = useState("")
  const [reviewerDrawerFolderPath, setReviewerDrawerFolderPath] = useState<string | null>(null)
  const [reviewerDrawerCompanyName, setReviewerDrawerCompanyName] = useState("")
  const [reviewerDrawerCompanyId, setReviewerDrawerCompanyId] = useState<number | null>(null)
  const [reviewerDrawerLatestFmv, setReviewerDrawerLatestFmv] = useState<number | null>(null)
  const [reviewerDrawerLatestFmvDate, setReviewerDrawerLatestFmvDate] = useState<string | null>(null)
  const [reviewerEmailSearch, setReviewerEmailSearch] = useState("")
  const reviewerEmailsQuery = useClientEmails(reviewerDrawerEmail)

  // Proof status from DB
  const { data: reviewerProofStatuses } = useProofStatuses(viewAs || "all")

  const toggleExpand = (companyId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(companyId)) next.delete(companyId)
      else next.add(companyId)
      return next
    })
  }

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "company" ? "asc" : "desc")
    }
  }

  const scrollToTop = () => {
    tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const requestAction = (entry: FmvReviewEntry, type: ActionType) => {
    setConfirmAction({ entry, type })
  }

  const handleConfirm = async (payload?: FixPayload) => {
    if (!confirmAction) return
    const { entry, type } = confirmAction

    if (type === "undo") {
      setActedIds((prev) => {
        const next = new Set(prev)
        next.delete(entry.companyId)
        return next
      })
      setConfirmAction(null)
      return
    }

    // Execute or mitigate — call API if payload provided
    if (type === "execute" && payload) {
      setFixPending(true)
      try {
        await applyFmvFix(entry.companyId, payload.fixOption, {
          irrTarget: payload.irrTarget ? parseFloat(payload.irrTarget) : undefined,
          fmvValue: payload.fmvTarget ? parseFloat(payload.fmvTarget) : undefined,
          fmvName: payload.fmvName,
          fmvComment: payload.fmvComment,
          fmvDate: payload.fmvDate,
          proofFileName: payload.proofFileName,
        })
        toaster.success({ title: "Fix applied", description: `${entry.companyName} updated` })
        queryClient.invalidateQueries({ queryKey: ["fmvReview"] })
        queryClient.invalidateQueries({ queryKey: ["fmvProgress"] })
        queryClient.invalidateQueries({ queryKey: ["fmvPunchlist"] })
      } catch (err: any) {
        toaster.error({ title: "Fix failed", description: err?.message || "Unknown error" })
        setFixPending(false)
        setConfirmAction(null)
        return
      }
      setFixPending(false)
    }

    setActedIds((prev) => new Set(prev).add(entry.companyId))
    setConfirmAction(null)
  }

  const handleCorrectDate = async (entry: ReconcilableEntry, newDate: string, proofFileName: string) => {
    if (!entry.latestFmvId) {
      toaster.error({ title: "Cannot correct date", description: "No FMV record ID available" })
      return
    }
    try {
      await correctFmvDate(entry.companyId, entry.latestFmvId, newDate, proofFileName)
      toaster.success({ title: "Date corrected", description: `${entry.companyName} FMV date updated to ${newDate}` })
      queryClient.invalidateQueries({ queryKey: ["fmvReview"] })
      queryClient.invalidateQueries({ queryKey: ["fmvProgress"] })
      queryClient.invalidateQueries({ queryKey: ["proofStatuses"] })
    } catch (err: any) {
      toaster.error({ title: "Date correction failed", description: err?.message || "Unknown error" })
    }
  }

  const staleCount = useMemo(() => entries.filter((e) => e.isStale).length, [entries])
  const noFmvCount = useMemo(() => entries.filter((e) => e.latestFmv == null).length, [entries])
  const noIrrCount = useMemo(() => entries.filter((e) => e.reconciliation === "no_irr").length, [entries])
  const punchlistCount = useMemo(() => entries.filter((e) => e.hasPendingPunchlist).length, [entries])
  const reconcileCount = useMemo(
    () => entries.filter((e) =>
      !e.hasPendingPunchlist && (e.reconciliation === "irr_mismatch" || e.reconciliation === "discount_needed" || e.reconciliation === "no_irr" || e.reconciliation === "no_fmv")
    ).length,
    [entries],
  )
  const troubleCount = useMemo(() => entries.filter((e) => e.headcountDrop != null && e.headcountDrop <= -10).length, [entries])

  const filtered = useMemo(() => {
    let list = entries
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) => e.companyName.toLowerCase().includes(q))
    }
    if (filterStale) {
      list = list.filter((e) => e.isStale || e.latestFmv == null)
    }
    if (filterPunchlist) {
      list = list.filter((e) => e.hasPendingPunchlist)
    }
    if (filterReconcile) {
      list = list.filter((e) => !e.hasPendingPunchlist && (e.reconciliation === "irr_mismatch" || e.reconciliation === "discount_needed" || e.reconciliation === "no_irr" || e.reconciliation === "no_fmv"))
    }
    if (filterTrouble) {
      list = list.filter((e) => e.headcountDrop != null && e.headcountDrop <= -10)
    }
    return [...list].sort((a, b) =>
      compareSortValues(getSortValue(a, sortKey), getSortValue(b, sortKey), sortDir)
    )
  }, [entries, search, filterStale, filterPunchlist, filterReconcile, filterTrouble, sortKey, sortDir])

  // ─── DM View rendering ──────────────────────────────────────────────────────
  if (isDmView) {
    return (
      <DmFmvView
        entries={dmEntries}
        isLoading={isDmLoading}
        isSuperUser={isSuperUser}
        managers={managers}
        viewAs={viewAs}
        setViewAs={setViewAs}
        userId={dm?.id}
        testMode={testMode}
        setTestMode={setTestMode}
        stagingPendingCount={stagingPendingCount}
        punchlistPendingCount={punchlistPendingCount}
        microsoftConnected={microsoftConnected}
        microsoftLoading={microsoftLoading}
        microsoftConnect={microsoftConnect}
      />
    )
  }

  // ─── Reviewer View ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Section>
        <HStack gap={3} py={10} justifyContent="center">
          <Spinner size="md" />
          <Text>Loading FMV review data...</Text>
        </HStack>
      </Section>
    )
  }

  return (
    <Section h="100vh" overflow="hidden" display="flex" flexDirection="column">
      <Stack gap={4} flex={1} minH={0}>
        {/* Row 1: Title */}
        <Text fontSize="2xl" fontWeight="bold">FMV Review</Text>

        {/* Row 2: Tabs + View As + Reports (far right) */}
        <HStack gap={3} alignItems="center" justifyContent="space-between">
          <HStack gap={3} alignItems="center">
            <HStack gap={0} bg="rgba(255,255,255,0.05)" borderRadius="md" border="1px solid" borderColor="border.default" overflow="hidden">
              <Box
                as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
                bg={activeTab === "review" ? "rgba(59,130,246,0.15)" : "transparent"}
                color={activeTab === "review" ? "#3b82f6" : "text.muted"}
                borderRight="1px solid" borderColor="border.default"
                _hover={{ bg: activeTab === "review" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
                onClick={() => setActiveTab("review")}
              >
                Review
              </Box>
              <Box
                as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
                bg={activeTab === "needsproof" ? "rgba(59,130,246,0.15)" : "transparent"}
                color={activeTab === "needsproof" ? "#3b82f6" : "text.muted"}
                borderRight="1px solid" borderColor="border.default"
                _hover={{ bg: activeTab === "needsproof" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
                onClick={() => setActiveTab("needsproof")}
              >
                Needs Proof
                {(reviewerProofStatuses?.length ?? 0) > 0 && (
                  <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                    bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
                    {reviewerProofStatuses?.length ?? 0}
                  </Box>
                )}
              </Box>
              <Box
                as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
                bg={activeTab === "staging" ? "rgba(59,130,246,0.15)" : "transparent"}
                color={activeTab === "staging" ? "#3b82f6" : "text.muted"}
                borderRight="1px solid" borderColor="border.default"
                _hover={{ bg: activeTab === "staging" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
                onClick={() => setActiveTab("staging")}
              >
                FMV Staging
                {stagingPendingCount > 0 && (
                  <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                    bg="rgba(245,158,11,0.2)" color="#f59e0b" lineHeight="1">
                    {stagingPendingCount}
                  </Box>
                )}
              </Box>
              <Box
                as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
                bg={activeTab === "punchlist" ? "rgba(59,130,246,0.15)" : "transparent"}
                color={activeTab === "punchlist" ? "#3b82f6" : "text.muted"}
                borderRight="1px solid" borderColor="border.default"
                _hover={{ bg: activeTab === "punchlist" ? "rgba(59,130,246,0.2)" : "hover.bg" }}
                onClick={() => setActiveTab("punchlist")}
              >
                Punchlist
                {punchlistPendingCount > 0 && (
                  <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                    bg="rgba(239,68,68,0.2)" color="#ef4444" lineHeight="1">
                    {punchlistPendingCount}
                  </Box>
                )}
              </Box>
              <Box
                as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
                bg={activeTab === "activity" ? "rgba(139,92,246,0.15)" : "transparent"}
                color={activeTab === "activity" ? "#8b5cf6" : "text.muted"}
                _hover={{ bg: activeTab === "activity" ? "rgba(139,92,246,0.2)" : "hover.bg" }}
                onClick={() => setActiveTab("activity")}
              >
                Activity
                {reviewerUnseenCount > 0 && (
                  <Box as="span" ml={1.5} px={1.5} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="700"
                    bg="rgba(139,92,246,0.2)" color="#8b5cf6" lineHeight="1">
                    {reviewerUnseenCount}
                  </Box>
                )}
              </Box>
            </HStack>
            {isSuperUser && managers && (
              <Box as="select"
                value={viewAs || ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setViewAs(e.target.value || undefined)}
                bg="card.bg" border="1px solid" borderColor="border.default" borderRadius="md"
                px={2} py={1} fontSize="xs" color="text.muted" cursor="pointer"
                _hover={{ borderColor: "#7eb8ff" }}
              >
                <option value="">My View</option>
                <option value="all">All Companies</option>
                {managers.map((m) => (
                  <option key={m.id} value={String(m.id)}>{m.name}</option>
                ))}
              </Box>
            )}
            {isSuperUser && (
              <Box
                as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="700" cursor="pointer" whiteSpace="nowrap"
                bg={testMode ? "rgba(245,158,11,0.15)" : "rgba(0,0,0,0.03)"}
                color={testMode ? "#f59e0b" : "#9ca3af"}
                border={`1px solid ${testMode ? "rgba(245,158,11,0.4)" : "#e5e7eb"}`}
                _hover={{ opacity: 0.85 }}
                onClick={() => setTestMode((p) => !p)}
                title={testMode ? "Test mode ON — ICON Aircraft visible in staging" : "Enable test mode"}
              >
                {testMode ? "Test ON" : "Test"}
              </Box>
            )}
          </HStack>
          <Box
            as="button" px={3} py={1} fontSize="xs" fontWeight="600" cursor="pointer"
            bg={activeTab === "reports" ? "rgba(20,184,166,0.15)" : "transparent"}
            color={activeTab === "reports" ? "#14b8a6" : "text.muted"}
            borderRadius="md" border="1px solid"
            borderColor={activeTab === "reports" ? "rgba(20,184,166,0.4)" : "border.default"}
            _hover={{ bg: activeTab === "reports" ? "rgba(20,184,166,0.2)" : "hover.bg" }}
            onClick={() => setActiveTab("reports")}
          >
            Reports
          </Box>
        </HStack>

        {/* Row 3: Search + count + filter buttons (review tab only) */}
        {activeTab === "review" && (
          <HStack gap={3} justifyContent="space-between" alignItems="center">
            <HStack gap={3} alignItems="center" flex={0} flexShrink={0}>
              <Input
                placeholder="Search companies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                w="240px"
                size="sm"
              />
              <Text fontSize="sm" color="text.muted" whiteSpace="nowrap">{filtered.length} companies</Text>
            </HStack>
            <HStack gap={2} flexShrink={0}>
              <Box
                as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" whiteSpace="nowrap"
                bg={filterStale ? "rgba(239,68,68,0.15)" : "transparent"}
                border={filterStale ? "1px solid rgba(239,68,68,0.4)" : "1px solid"}
                borderColor={filterStale ? "rgba(239,68,68,0.4)" : "border.default"}
                color={filterStale ? "#ef4444" : "text.muted"}
                onClick={() => { const next = !filterStale; setFilterStale(next); if (next) { setFilterReconcile(false); setFilterPunchlist(false); setFilterTrouble(false) } }}
                _hover={{ bg: filterStale ? "rgba(239,68,68,0.2)" : "hover.bg" }}
              >
                Stale ({staleCount})
              </Box>
              {isReviewer && reconcileCount > 0 && (
                <Box
                  as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" whiteSpace="nowrap"
                  bg={filterReconcile ? "rgba(168,85,247,0.15)" : "transparent"}
                  border={filterReconcile ? "1px solid rgba(168,85,247,0.4)" : "1px solid"}
                  borderColor={filterReconcile ? "rgba(168,85,247,0.4)" : "border.default"}
                  color={filterReconcile ? "#a855f7" : "text.muted"}
                  onClick={() => { const next = !filterReconcile; setFilterReconcile(next); if (next) { setFilterStale(false); setFilterPunchlist(false); setFilterTrouble(false) } }}
                  _hover={{ bg: filterReconcile ? "rgba(168,85,247,0.2)" : "hover.bg" }}
                >
                  Reconcile ({reconcileCount})
                </Box>
              )}
              {isReviewer && punchlistCount > 0 && (
                <Box
                  as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" whiteSpace="nowrap"
                  bg={filterPunchlist ? "rgba(245,158,11,0.15)" : "transparent"}
                  border={filterPunchlist ? "1px solid rgba(245,158,11,0.4)" : "1px solid"}
                  borderColor={filterPunchlist ? "rgba(245,158,11,0.4)" : "border.default"}
                  color={filterPunchlist ? "#f59e0b" : "text.muted"}
                  onClick={() => { const next = !filterPunchlist; setFilterPunchlist(next); if (next) { setFilterStale(false); setFilterReconcile(false); setFilterTrouble(false) } }}
                  _hover={{ bg: filterPunchlist ? "rgba(245,158,11,0.2)" : "hover.bg" }}
                >
                  In Punchlist ({punchlistCount})
                </Box>
              )}
              {troubleCount > 0 && (
                <Box
                  as="button" px={3} py={1} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" whiteSpace="nowrap"
                  bg={filterTrouble ? "rgba(220,38,38,0.15)" : "transparent"}
                  border={filterTrouble ? "1px solid rgba(220,38,38,0.4)" : "1px solid"}
                  borderColor={filterTrouble ? "rgba(220,38,38,0.4)" : "border.default"}
                  color={filterTrouble ? "#dc2626" : "text.muted"}
                  onClick={() => { const next = !filterTrouble; setFilterTrouble(next); if (next) { setFilterStale(false); setFilterReconcile(false); setFilterPunchlist(false) } }}
                  _hover={{ bg: filterTrouble ? "rgba(220,38,38,0.2)" : "hover.bg" }}
                >
                  In Trouble ({troubleCount})
                </Box>
              )}
            </HStack>
          </HStack>
        )}

        {activeTab === "review" && (<>

        {/* Fixed header */}
        <Table.Root size="md" variant="outline" style={{ tableLayout: "fixed" }}>
          <ColGroup />
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="30px" />
              <SortableHeader label="Company" sortKey="company" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Current FMV" sortKey="fmv" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Age" sortKey="age" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="IRR FMV" sortKey="irrFmv" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="Disc. FMV" sortKey="discFmv" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Deals" sortKey="deals" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="Portfolio" sortKey="portfolio" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
              <SortableHeader label="Invested" sortKey="invested" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
            </Table.Row>
          </Table.Header>
        </Table.Root>

        {/* Scrollable body */}
        <Box ref={tableScrollRef} flex={1} minH={0} overflowY="auto">
          <Table.Root size="md" variant="outline" style={{ tableLayout: "fixed", borderTop: "none" }}>
            <ColGroup />
            <Table.Body>
              {filtered.map((entry) => {
                const isExpanded = expandedIds.has(entry.companyId)
                const badge = reconBadgeStyle(entry.reconciliation)
                return (
                  <>
                    {/* Main row — click anywhere to expand/collapse */}
                    <Table.Row
                      key={entry.companyId}
                      cursor="pointer"
                      _hover={{ bg: "hover.bg" }}
                      onClick={() => toggleExpand(entry.companyId)}
                    >
                      {/* Expand chevron */}
                      <Table.Cell w="30px" py={3}>
                        <Icon
                          as={(isExpanded ? FaChevronDown : FaChevronRight) as React.ElementType}
                          boxSize="10px"
                          color="text.muted"
                        />
                      </Table.Cell>

                      {/* Company name */}
                      <Table.Cell py={3}>
                        <HStack gap={1.5}>
                          <Text fontSize="sm" fontWeight="500">
                            {entry.companyName}
                          </Text>
                          {entry.dmNotes && entry.dmNotes.length > 0 && (
                            <Box as="span" color="#9ca3af" title={`${entry.dmNotes.length} DM note(s)`}>
                              <FaRegStickyNote size={12} />
                            </Box>
                          )}
                        </HStack>
                      </Table.Cell>

                      {/* 409A FMV */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm">{formatFmv(entry.latestFmv)}</Text>
                      </Table.Cell>

                      {/* Date */}
                      <Table.Cell py={3}>
                        <Text fontSize="sm" color="text.muted">{formatDate(entry.latestFmvDate)}</Text>
                      </Table.Cell>

                      {/* Age */}
                      <Table.Cell textAlign="right" py={3}>
                        {entry.latestFmvDate ? (
                          <Box
                            as="span"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight="600"
                            bg={ageBg(entry.staleness.yearsStale)}
                            color={ageColor(entry.staleness.yearsStale)}
                          >
                            {ageLabel(entry.staleness.yearsStale)}
                          </Box>
                        ) : (
                          <Text fontSize="sm" color="#6b7280">N/A</Text>
                        )}
                      </Table.Cell>

                      {/* IRR FMV */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm">{formatFmv(entry.irrFmv)}</Text>
                      </Table.Cell>

                      {/* Discounted FMV */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm" color="text.muted">{formatFmv(entry.discountedFmv)}</Text>
                      </Table.Cell>

                      {/* Status + Action */}
                      <Table.Cell py={3}>
                        {(() => {
                          const suggestion = getLevel1Suggestion(entry)
                          const acted = actedIds.has(entry.companyId)

                          // Entry is OK or already acted on
                          if (!suggestion) {
                            return <Text fontSize="xs" fontWeight="600" color="#22c55e">OK</Text>
                          }

                          if (acted) {
                            return (
                              <HStack gap={2}>
                                <Text fontSize="xs" fontWeight="600" color="#22c55e">Done</Text>
                                <Box
                                  as="button"
                                  px={2}
                                  py={0.5}
                                  borderRadius="sm"
                                  fontSize="10px"
                                  fontWeight="600"
                                  bg="rgba(107,114,128,0.12)"
                                  color="#9ca3af"
                                  border="1px solid rgba(107,114,128,0.25)"
                                  cursor="pointer"
                                  _hover={{ bg: "rgba(107,114,128,0.2)" }}
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    requestAction(entry, "undo")
                                  }}
                                >
                                  <HStack gap={1}>
                                    <Icon as={FaUndo as React.ElementType} boxSize="8px" />
                                    <span>Undo</span>
                                  </HStack>
                                </Box>
                              </HStack>
                            )
                          }

                          // Company has a pending punchlist item — show "Queued" badge
                          if (entry.hasPendingPunchlist) {
                            const actionType = isReviewer ? getActionType(entry.reconciliation) : null
                            return (
                              <HStack gap={2}>
                                <Box px={2} py={0.5} borderRadius="sm" fontSize="10px" fontWeight="700"
                                  bg="rgba(245,158,11,0.12)" color="#f59e0b"
                                  border="1px solid rgba(245,158,11,0.3)">
                                  Queued
                                </Box>
                                {actionType && (
                                  <Box
                                    as="button" px={2} py={0.5} borderRadius="sm" fontSize="10px" fontWeight="600"
                                    flexShrink={0} bg="rgba(107,114,128,0.08)" color="#9ca3af"
                                    border="1px solid rgba(107,114,128,0.2)" cursor="pointer"
                                    _hover={{ bg: "rgba(107,114,128,0.15)" }}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation()
                                      requestAction(entry, actionType)
                                    }}
                                  >
                                    Re-fix
                                  </Box>
                                )}
                              </HStack>
                            )
                          }

                          const actionType = isReviewer ? getActionType(entry.reconciliation) : null
                          return (
                            <HStack gap={2}>
                              <HStack gap={1} flex={1} minW={0}>
                                <Icon as={FaExclamationTriangle as React.ElementType} boxSize="9px" color={badge.color} flexShrink={0} />
                                <Text fontSize="xs" fontWeight="600" color={badge.color} truncate>{suggestion}</Text>
                              </HStack>
                              {actionType && (
                                <Box
                                  as="button"
                                  px={2}
                                  py={0.5}
                                  borderRadius="sm"
                                  fontSize="10px"
                                  fontWeight="700"
                                  flexShrink={0}
                                  bg={actionType === "execute" ? "rgba(34,197,94,0.12)" : "rgba(59,130,246,0.12)"}
                                  color={actionType === "execute" ? "#22c55e" : "#3b82f6"}
                                  border={`1px solid ${actionType === "execute" ? "rgba(34,197,94,0.3)" : "rgba(59,130,246,0.3)"}`}
                                  cursor="pointer"
                                  _hover={{ bg: actionType === "execute" ? "rgba(34,197,94,0.2)" : "rgba(59,130,246,0.2)" }}
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    requestAction(entry, actionType)
                                  }}
                                >
                                  {actionType === "execute" ? "Fix" : "Mitigate"}
                                </Box>
                              )}
                            </HStack>
                          )
                        })()}
                      </Table.Cell>

                      {/* Active deals */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm">{entry.activeDealCount || "—"}</Text>
                      </Table.Cell>

                      {/* Portfolio */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm">{entry.portfolioDealCount || "—"}</Text>
                      </Table.Cell>

                      {/* Invested */}
                      <Table.Cell textAlign="right" py={3}>
                        <Text fontSize="sm">{formatDollars(entry.totalInvested)}</Text>
                      </Table.Cell>
                    </Table.Row>

                    {/* Expandable proof files + reconciliation */}
                    {isExpanded && (
                      <>
                      <Table.Row>
                        <Table.Cell colSpan={11} p={0} bg="rgba(255,255,255,0.02)">
                          <ProofFilesRow entry={entry} onRequestFix={isReviewer ? (e) => requestAction(e, "execute") : undefined} onCorrectDate={handleCorrectDate} />
                        </Table.Cell>
                      </Table.Row>
                      {entry.dmNotes && entry.dmNotes.length > 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={11} px={6} py={3} bg="rgba(255,255,255,0.02)">
                            <Stack gap={2}>
                              <Text fontSize="xs" fontWeight="600" color="text.muted" textTransform="uppercase" letterSpacing="0.05em">DM Notes</Text>
                              {entry.dmNotes.map((n) => (
                                <HStack key={`${n.clientId}-${n.companyId}`} gap={2} px={3} py={1.5} bg="rgba(255,255,255,0.03)" borderRadius="md" borderLeft="2px solid" borderColor="rgba(107,114,128,0.3)">
                                  <Text fontSize="xs" color="text.muted" flexShrink={0} fontWeight="500">{n.dmName}:</Text>
                                  <Text fontSize="xs" color="text.muted">{n.note}</Text>
                                  <Text fontSize="2xs" color="text.muted" flexShrink={0} ml="auto">{new Date(n.updatedAt).toLocaleDateString()}</Text>
                                </HStack>
                              ))}
                            </Stack>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      </>
                    )}
                  </>
                )
              })}
            </Table.Body>
          </Table.Root>

          {filtered.length === 0 && (
            <Text fontSize="sm" color="text.muted" textAlign="center" py={8}>
              {search || filterStale || filterReconcile || filterTrouble ? "No companies match the current filters" : "No companies found"}
            </Text>
          )}
        </Box>
        </>)}

        {activeTab === "punchlist" && <FmvPunchlistTab />}
        {activeTab === "needsproof" && (
          <NeedsProofTab
            viewAs={viewAs || "all"}
            onOpenEmailDrawer={(email, name, folderPath, companyName, companyId, fmv) => { setReviewerDrawerEmail(email); setReviewerDrawerName(name); setReviewerDrawerFolderPath(folderPath); setReviewerDrawerCompanyName(companyName); setReviewerDrawerCompanyId(companyId); setReviewerDrawerLatestFmv(fmv?.value ?? null); setReviewerDrawerLatestFmvDate(fmv?.date ?? null) }}
          />
        )}
        {activeTab === "staging" && <FmvStagingTab viewAs={viewAs} isReviewer={true} testMode={testMode} />}
        {activeTab === "activity" && <Box flex={1} minH={0} overflowY="auto"><FmvActivityTab /></Box>}
        {activeTab === "reports" && <Box flex={1} minH={0} overflowY="auto"><FmvChangeReportTab /></Box>}
      </Stack>

      {/* Reviewer email drawer (for Needs Proof tab) */}
      <Drawer.Root open={!!reviewerDrawerEmail} onOpenChange={(e) => { if (!e.open) { setReviewerDrawerEmail(null); setReviewerDrawerName(""); setReviewerDrawerFolderPath(null); setReviewerDrawerCompanyName(""); setReviewerDrawerCompanyId(null); setReviewerDrawerLatestFmv(null); setReviewerDrawerLatestFmvDate(null); setReviewerEmailSearch("") } }} size="md">
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content bg="card.bg">
              <Drawer.Header px={4} pt={4} pb={2} borderBottom="1px solid" borderColor="border.default">
                <Drawer.Title fontFamily="body" fontSize="md" fontWeight="600">
                  Emails with {reviewerDrawerName}
                </Drawer.Title>
                <CloseButton size="sm" position="absolute" top={3} right={3} onClick={() => { setReviewerDrawerEmail(null); setReviewerDrawerName(""); setReviewerDrawerFolderPath(null); setReviewerDrawerCompanyName(""); setReviewerDrawerCompanyId(null); setReviewerDrawerLatestFmv(null); setReviewerDrawerLatestFmvDate(null); setReviewerEmailSearch("") }} />
              </Drawer.Header>
              <Drawer.Body px={4} py={3}>
                {reviewerEmailsQuery.isLoading ? (
                  <HStack gap={2} py={8} justifyContent="center"><Spinner size="sm" /><Text fontSize="sm">Loading emails...</Text></HStack>
                ) : reviewerEmailsQuery.data && !reviewerEmailsQuery.data.microsoftConnected ? (
                  <Stack gap={3} py={10} alignItems="center">
                    <Text fontSize="sm" color="text.muted">Microsoft account not connected.</Text>
                    <Text fontSize="xs" color="text.muted">Connect your Outlook account from the Deal Page to view emails.</Text>
                  </Stack>
                ) : reviewerEmailsQuery.data?.emails.length === 0 ? (
                  <Text fontSize="sm" color="text.muted" py={8} textAlign="center">No emails found</Text>
                ) : (() => {
                  const allEmails = reviewerEmailsQuery.data?.emails || []
                  const term = reviewerEmailSearch.toLowerCase().trim()
                  const filtered = term
                    ? allEmails.filter((e) =>
                        (e.subject || "").toLowerCase().includes(term) ||
                        e.bodyPreview.toLowerCase().includes(term) ||
                        (e.fromName || "").toLowerCase().includes(term) ||
                        e.fromAddress.toLowerCase().includes(term)
                      )
                    : allEmails
                  return (
                    <Stack gap={2}>
                      <Input
                        size="sm" placeholder="Search emails..." value={reviewerEmailSearch}
                        onChange={(e) => setReviewerEmailSearch(e.target.value)}
                      />
                      {term && (
                        <Text fontSize="xs" color="text.muted">{filtered.length} of {allEmails.length} emails</Text>
                      )}
                      {filtered.map((email) => (
                        <EmailCard key={email.id} email={email} companyFolderPath={reviewerDrawerFolderPath} companyName={reviewerDrawerCompanyName} companyId={reviewerDrawerCompanyId} latestFmv={reviewerDrawerLatestFmv} latestFmvDate={reviewerDrawerLatestFmvDate} />
                      ))}
                    </Stack>
                  )
                })()}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      {/* Scroll to top */}
      <IconButton
        aria-label="Scroll to top"
        size="sm"
        position="fixed"
        bottom="20px"
        right="20px"
        onClick={scrollToTop}
        borderRadius="full"
        opacity={0.7}
        _hover={{ opacity: 1 }}
      >
        <FaArrowUp />
      </IconButton>

      {/* Confirm action modal */}
      <Dialog.Root open={!!confirmAction} onOpenChange={(e) => !e.open && setConfirmAction(null)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="500px">
              <Dialog.Header px={6} pt={5} pb={3}>
                <Dialog.Title fontFamily="body" fontWeight="semibold">
                  {confirmAction?.type === "execute"
                    ? `Fix: ${confirmAction.entry.companyName}`
                    : confirmAction?.type === "mitigate"
                      ? `Mitigate: ${confirmAction.entry.companyName}`
                      : `Undo: ${confirmAction?.entry.companyName}`}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body px={6} pb={6}>
                {confirmAction && (
                  <ConfirmModalBody
                    key={`${confirmAction.entry.companyId}-${confirmAction.type}`}
                    action={confirmAction}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={handleConfirm}
                    existingPunchlistItem={punchlistData?.pending?.find(
                      (p) => p.companyId === confirmAction.entry.companyId
                    ) || null}
                  />
                )}
              </Dialog.Body>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Section>
  )
}
